import * as poseDetection from '@tensorflow-models/pose-detection';
import * as tf from '@tensorflow/tfjs-core';
import '@tensorflow/tfjs-backend-webgl';
import '@tensorflow/tfjs-backend-cpu';

class MoveNetBodyProcessor {
  constructor() {
    this.detector = null;
    this.isInitialized = false;
  }

  async initialize() {
    if (this.isInitialized && this.detector) {
      return true;
    }

    try {
      console.log('🔄 Starting TensorFlow.js and MoveNet initialization...');
      
      // Für mobile Geräte: Nur CPU Backend verwenden (WebGL hat oft Probleme)
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      const backends = isMobile ? ['cpu'] : ['webgl', 'cpu'];
      
      console.log(`📱 Device type: ${isMobile ? 'Mobile' : 'Desktop'}, trying backends:`, backends);
      
      let backendInitialized = false;
      
      for (const backend of backends) {
        try {
          console.log(`🔄 Trying backend: ${backend}...`);
          await tf.setBackend(backend);
          await tf.ready();
          console.log(`✅ ${backend.toUpperCase()} backend initialized successfully`);
          backendInitialized = true;
          break;
        } catch (backendError) {
          console.warn(`⚠️ ${backend.toUpperCase()} backend failed:`, backendError.message);
        }
      }
      
      if (!backendInitialized) {
        throw new Error('Could not initialize any TensorFlow.js backend');
      }
      
      console.log('🔄 Creating pose detector...');
      
      // Select model based on available backend
      // MoveNet works reliably with CPU backend (recommended for CPU-only environments)
      // BlazePose requires WebGL for proper inference
      const currentBackend = tf.getBackend();
      
      if (currentBackend === 'cpu') {
        // Use MoveNet for CPU backend - best compatibility and performance
        console.log('📦 Using MoveNet THUNDER (17 keypoints, optimized for CPU backend)');
        this.detector = await poseDetection.createDetector(
          poseDetection.SupportedModels.MoveNet,
          {
            modelType: poseDetection.movenet.modelType.SINGLEPOSE_THUNDER,
            enableSmoothing: true,
            minPoseScore: 0.3
          }
        );
        this.isInitialized = true;
        console.log('✅ MoveNet THUNDER (17 KEYPOINTS) initialized successfully with CPU backend');
      } else {
        // Use BlazePose for WebGL backend - higher accuracy with GPU
        console.log('📦 Using BlazePose FULL (33 keypoints, optimized for WebGL backend)');
        this.detector = await poseDetection.createDetector(
          poseDetection.SupportedModels.BlazePose,
          {
            runtime: 'tfjs',
            modelType: 'full',
            enableSmoothing: true,
            enableSegmentation: false,
            smoothSegmentation: false
          }
        );
        this.isInitialized = true;
        console.log('✅ BlazePose FULL (33 KEYPOINTS) initialized successfully with WebGL backend');
      }
      
      return true;

    } catch (error) {
      console.error('❌ MoveNet initialization failed:', error);
      this.isInitialized = false;
      return false;
    }
  }

  // Live pose detection for camera feed with green body outline
  async detectAndDrawPose(videoElement, canvasElement) {
    if (!this.isInitialized || !this.detector) {
      console.warn('MoveNet not initialized for live detection');
      return false;
    }

    try {
      const poses = await this.detector.estimatePoses(videoElement);
      
      if (poses.length > 0 && poses[0].keypoints) {
        this.drawBodyOutline(canvasElement, poses[0].keypoints, videoElement.videoWidth, videoElement.videoHeight);
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Live pose detection error:', error);
      return false;
    }
  }

  // Draw green outline around detected body
  drawBodyOutline(canvas, keypoints, videoWidth, videoHeight) {
    const ctx = canvas.getContext('2d');
    
    // Set canvas size to match video
    canvas.width = videoWidth;
    canvas.height = videoHeight;
    
    // Clear previous drawings
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Filter confident keypoints
    const confidentKeypoints = keypoints.filter(kp => kp.score > 0.3);
    
    if (confidentKeypoints.length < 5) return; // Need enough points for outline
    
    // Define body outline connections
    const bodyConnections = [
      [0, 1], [0, 2], // Head connections
      [1, 3], [2, 4], // Eye to ear
      [5, 6], // Shoulders
      [5, 7], [7, 9], // Left arm
      [6, 8], [8, 10], // Right arm
      [5, 11], [6, 12], // Shoulder to hip
      [11, 12], // Hip line
      [11, 13], [13, 15], // Left leg
      [12, 14], [14, 16], // Right leg
    ];
    
    // Draw green outline with thick lines
    ctx.strokeStyle = '#00FF88';
    ctx.lineWidth = 4;  // Dickere Linien
    ctx.lineCap = 'round';
    ctx.shadowColor = '#00FF88';
    ctx.shadowBlur = 8;
    
    // Draw connections
    bodyConnections.forEach(([startIdx, endIdx]) => {
      const startPoint = keypoints[startIdx];
      const endPoint = keypoints[endIdx];
      
      if (startPoint && endPoint && startPoint.score > 0.3 && endPoint.score > 0.3) {
        ctx.beginPath();
        ctx.moveTo(startPoint.x, startPoint.y);
        ctx.lineTo(endPoint.x, endPoint.y);
        ctx.stroke();
      }
    });
    
    // Draw keypoints as larger circles
    ctx.fillStyle = '#00FF88';
    confidentKeypoints.forEach(kp => {
      ctx.beginPath();
      ctx.arc(kp.x, kp.y, 5, 0, 2 * Math.PI);  // Größere Kreise
      ctx.fill();
    });
    
    // Draw body bounding box (without label)
    this.drawBodyBoundingBox(ctx, confidentKeypoints);
  }

  // Draw green bounding box around entire body (ohne Label)
  drawBodyBoundingBox(ctx, keypoints) {
    if (keypoints.length === 0) return;
    
    let minX = Infinity, maxX = -Infinity;
    let minY = Infinity, maxY = -Infinity;
    
    keypoints.forEach(kp => {
      minX = Math.min(minX, kp.x);
      maxX = Math.max(maxX, kp.x);
      minY = Math.min(minY, kp.y);
      maxY = Math.max(maxY, kp.y);
    });
    
    // Add padding
    const padding = 20;
    minX = Math.max(0, minX - padding);
    maxX = Math.min(ctx.canvas.width, maxX + padding);
    minY = Math.max(0, minY - padding);
    maxY = Math.min(ctx.canvas.height, maxY + padding);
    
    // Draw bounding box (ohne Text-Label) - DICKER
    ctx.strokeStyle = '#00FF88';
    ctx.lineWidth = 3;  // Dickere Linie
    ctx.setLineDash([10, 5]);
    ctx.strokeRect(minX, minY, maxX - minX, maxY - minY);
    ctx.setLineDash([]); // Reset line dash
    
    // Text-Label entfernt
  }

  // Detect body in uploaded image and check if within boundaries
  async detectBodyInImage(imageSrc, canvasElement) {
    try {
      // Initialize MoveNet if not done yet
      if (!this.isInitialized) {
        const success = await this.initialize();
        if (!success) {
          return {
            bodyDetected: false,
            withinBoundaries: false,
            message: '❌ Körpererkennung konnte nicht initialisiert werden'
          };
        }
      }

      // Load image
      const img = await this.loadImage(imageSrc);
      
      console.log(`🔄 Analyzing image for body detection (${img.width}x${img.height})...`);

      // Detect poses with MoveNet THUNDER (high accuracy)
      const poses = await this.detector.estimatePoses(img);

      if (poses.length === 0 || !poses[0].keypoints) {
        console.warn('❌ No body detected in image');
        return {
          bodyDetected: false,
          withinBoundaries: false,
          message: '❌ Kein Körper erkannt - bitte Ganzkörperfoto hochladen'
        };
      }

      const keypoints = poses[0].keypoints;
      const poseScore = poses[0].score || 0;
      
      const currentBackend = tf.getBackend();
      const keypointCount = keypoints.length;
      console.log(`🔍 Pose detection score: ${(poseScore * 100).toFixed(1)}% (${currentBackend === 'cpu' ? 'MoveNet' : 'BlazePose'} - ${keypointCount} keypoints)`);
      console.log(`📊 Total keypoints available: ${keypointCount}`);
      
      // Filter high-confidence keypoints
      const confidentKeypoints = keypoints.filter(kp => kp.score > 0.4);
      
      console.log(`✅ Found ${confidentKeypoints.length} high-confidence keypoints (score > 0.4) out of ${keypoints.length}`);

      // Require minimum number of keypoints for accurate detection
      const minKeypoints = currentBackend === 'cpu' ? 8 : 10; // MoveNet (17 points) vs BlazePose (33 points)
      if (confidentKeypoints.length < minKeypoints) {
        console.warn(`⚠️ Insufficient keypoints: ${confidentKeypoints.length} (minimum ${minKeypoints} required)`);
        return {
          bodyDetected: false,
          withinBoundaries: false,
          message: `⚠️ Körper nicht klar erkennbar (nur ${confidentKeypoints.length} Punkte erkannt)`
        };
      }

      // Check if this is a full-body pose (has both upper and lower body keypoints)
      const hasUpperBody = confidentKeypoints.some(kp => kp.name && kp.name.includes('shoulder'));
      const hasLowerBody = confidentKeypoints.some(kp => kp.name && kp.name.includes('ankle'));
      
      if (!hasUpperBody || !hasLowerBody) {
        console.warn('⚠️ Not a full-body image (missing upper or lower body)');
        return {
          bodyDetected: true,
          withinBoundaries: false,
          message: '⚠️ Bitte vollständiges Ganzkörperfoto verwenden (Kopf bis Füße)'
        };
      }

      // Setup canvas to draw the outline
      const ctx = canvasElement.getContext('2d');
      canvasElement.width = img.width;
      canvasElement.height = img.height;

      // Draw the original image
      ctx.drawImage(img, 0, 0, img.width, img.height);

      // Draw green outline using existing method
      this.drawBodyOutlineOnImage(ctx, keypoints, img.width, img.height);

      // Check if body is within boundaries
      const boundaryCheck = this.checkBodyBoundaries(confidentKeypoints, img.width, img.height);

      console.log(`✅ Body detection complete: ${boundaryCheck.message}`);

      return {
        bodyDetected: true,
        withinBoundaries: boundaryCheck.withinBoundaries,
        message: boundaryCheck.message,
        keypoints: keypoints,
        poseScore: poseScore,
        keypointCount: confidentKeypoints.length
      };

    } catch (error) {
      console.error('❌ Body detection error:', error);
      return {
        bodyDetected: false,
        withinBoundaries: false,
        message: '❌ Fehler bei der Körpererkennung'
      };
    }
  }

  // Draw green outline on static image - adaptive for MoveNet (17 points) or BlazePose (33 points)
  drawBodyOutlineOnImage(ctx, keypoints, imageWidth, imageHeight) {
    // Filter confident keypoints with HIGHER threshold for accuracy
    const confidentKeypoints = keypoints.filter(kp => kp.score > 0.4);
    
    const currentBackend = tf.getBackend();
    const isMoveNet = currentBackend === 'cpu';
    const minKeypoints = isMoveNet ? 8 : 10;
    
    if (confidentKeypoints.length < minKeypoints) {
      console.warn('Not enough high-confidence keypoints detected:', confidentKeypoints.length);
      return;
    }
    
    console.log(`✅ Detected ${confidentKeypoints.length} high-confidence keypoints (${isMoveNet ? 'MoveNet 17-point' : 'BlazePose 33-point'} system)`);
    
    // MoveNet 17 keypoint connections (COCO format)
    const moveNetConnections = [
      [5, 6],   // Neck (shoulders)
      [5, 7],   // Left shoulder to elbow
      [7, 9],   // Left elbow to wrist
      [6, 8],   // Right shoulder to elbow
      [8, 10],  // Right elbow to wrist
      [5, 11],  // Left shoulder to hip
      [6, 12],  // Right shoulder to hip
      [11, 12], // Hips
      [11, 13], // Left hip to knee
      [13, 15], // Left knee to ankle
      [12, 14], // Right hip to knee
      [14, 16]  // Right knee to ankle
    ];
    
    // BlazePose 33 keypoint connections for full body detail
    const blazePoseConnections = [
      // Face (0-10)
      [0, 1], [1, 2], [2, 3], [3, 7],  // Right side of face
      [0, 4], [4, 5], [5, 6], [6, 8],  // Left side of face
      [9, 10],  // Mouth
      
      // Torso (11-24)
      [11, 12],  // Shoulders
      [11, 13], [13, 15],  // Right arm
      [12, 14], [14, 16],  // Left arm
      [11, 23], [12, 24],  // Shoulder to hip
      [23, 24],  // Hip line
      
      // Additional torso points for detail
      [11, 23], [12, 24],  // Torso sides
      
      // Legs (23-32)
      [23, 25], [25, 27],  // Right leg
      [24, 26], [26, 28],  // Left leg
      
      // Feet detail (27-32)
      [27, 29], [29, 31],  // Right foot
      [28, 30], [30, 32],  // Left foot
      [27, 31], [28, 32],  // Foot arches
    ];
    
    // Select appropriate connections based on model
    const connections = isMoveNet ? moveNetConnections : blazePoseConnections;
    const modelName = isMoveNet ? 'MoveNet' : 'BlazePose';
    
    // Draw green outline with thick lines
    ctx.strokeStyle = '#00FF88';
    ctx.lineWidth = 4;  // Dickere Linien
    ctx.lineCap = 'round';
    ctx.shadowColor = '#00FF88';
    ctx.shadowBlur = 8;
    
    // Draw connections
    let connectionsDrawn = 0;
    connections.forEach(([startIdx, endIdx]) => {
      if (startIdx < keypoints.length && endIdx < keypoints.length) {
        const startPoint = keypoints[startIdx];
        const endPoint = keypoints[endIdx];
        
        // Use higher confidence threshold for drawing
        if (startPoint && endPoint && startPoint.score > 0.4 && endPoint.score > 0.4) {
          ctx.beginPath();
          ctx.moveTo(startPoint.x, startPoint.y);
          ctx.lineTo(endPoint.x, endPoint.y);
          ctx.stroke();
          connectionsDrawn++;
        }
      }
    });
    
    console.log(`✅ Drew ${connectionsDrawn} body connections with ${modelName} system`);
    
    // Draw keypoints as larger circles
    ctx.fillStyle = '#00FF88';
    confidentKeypoints.forEach(kp => {
      ctx.beginPath();
      ctx.arc(kp.x, kp.y, 5, 0, 2 * Math.PI);  // Größere Kreise
      ctx.fill();
    });
    
    // Draw body bounding box (without label)
    this.drawBodyBoundingBox(ctx, confidentKeypoints);
  }

  // Check if body is within picture boundaries with detailed validation
  checkBodyBoundaries(keypoints, imageWidth, imageHeight) {
    let minX = Infinity, maxX = -Infinity;
    let minY = Infinity, maxY = -Infinity;
    
    keypoints.forEach(kp => {
      minX = Math.min(minX, kp.x);
      maxX = Math.max(maxX, kp.x);
      minY = Math.min(minY, kp.y);
      maxY = Math.max(maxY, kp.y);
    });

    // Define acceptable margin (5% of image dimensions for high-quality photos)
    const marginX = imageWidth * 0.05;
    const marginY = imageHeight * 0.05;
    
    // Calculate body dimensions
    const bodyWidth = maxX - minX;
    const bodyHeight = maxY - minY;
    const bodyArea = bodyWidth * bodyHeight;
    const imageArea = imageWidth * imageHeight;
    const bodyCoverage = (bodyArea / imageArea) * 100;
    
    console.log(`📊 Body dimensions: ${bodyWidth.toFixed(0)}x${bodyHeight.toFixed(0)} (${bodyCoverage.toFixed(1)}% of image)`);

    // Check if body is too small in the frame
    if (bodyCoverage < 15) {
      return {
        withinBoundaries: false,
        message: '⚠️ Körper zu klein im Bild - bitte näher herangehen'
      };
    }

    // Check if body is too large (likely cropped)
    if (bodyCoverage > 90) {
      return {
        withinBoundaries: false,
        message: '⚠️ Körper zu groß - bitte mehr Abstand für vollständige Aufnahme'
      };
    }

    // Check if body is outside image boundaries
    const isOutside = minX < 0 || maxX > imageWidth || minY < 0 || maxY > imageHeight;
    
    if (isOutside) {
      return {
        withinBoundaries: false,
        message: '⚠️ Körper ist außerhalb des Bildes - bitte vollständig im Rahmen'
      };
    }

    // Check if body is too close to edges
    const tooCloseToLeft = minX < marginX;
    const tooCloseToRight = maxX > (imageWidth - marginX);
    const tooCloseToTop = minY < marginY;
    const tooCloseToBottom = maxY > (imageHeight - marginY);

    if (tooCloseToLeft || tooCloseToRight || tooCloseToTop || tooCloseToBottom) {
      const directions = [];
      if (tooCloseToLeft) directions.push('links');
      if (tooCloseToRight) directions.push('rechts');
      if (tooCloseToTop) directions.push('oben');
      if (tooCloseToBottom) directions.push('unten');
      
      return {
        withinBoundaries: false,
        message: `⚠️ Körper zu nah am Rand (${directions.join(', ')}) - bitte mehr Abstand`
      };
    }

    // Perfect body positioning
    return {
      withinBoundaries: true,
      message: `✅ Perfekt! Körper vollständig erfasst (${keypoints.length} von 33 BlazePose-Punkten erkannt)`
    };
  }

  async transformImage(imageSrc, weightLossPercentage, treatmentCount = 10, weightLossKg = null, aiParams = null) {
    try {
      // Initialize MoveNet if not done yet
      if (!this.isInitialized) {
        const success = await this.initialize();
        if (!success) {
          return this.createFallbackTransformation(imageSrc, weightLossPercentage, treatmentCount);
        }
      }
      
      // Load image
      const img = await this.loadImage(imageSrc);
      
      // MOBILE OPTIMIZATION: Resize large images to prevent crashes
      const maxDimension = 2048; // Max 2048px for mobile compatibility
      let processedImg = img;
      
      if (img.width > maxDimension || img.height > maxDimension) {
        console.log(`⚠️ Image too large (${img.width}x${img.height}), resizing for mobile...`);
        processedImg = await this.resizeImage(img, maxDimension);
        console.log(`✅ Resized to ${processedImg.width}x${processedImg.height}`);
      }
      
      // Detect poses with MoveNet
      const poses = await this.detector.estimatePoses(processedImg);
      
      if (poses.length > 0 && poses[0].keypoints) {
        // Use AI-guided transformation if AI params provided
        if (aiParams) {
          console.log('🤖 Using AI-guided transformation with Gemini parameters');
          return this.applyAIGuidedTransformation(processedImg, poses[0].keypoints, aiParams, weightLossKg);
        } else {
          return this.applyMoveNetSlimming(processedImg, poses[0].keypoints, weightLossPercentage, treatmentCount, weightLossKg);
        }
      } else {
        return this.createSmartTransformation(processedImg, weightLossPercentage, treatmentCount, weightLossKg);
      }

    } catch (error) {
      console.error('MoveNet processing error:', error);
      return this.createFallbackTransformation(imageSrc, weightLossPercentage, treatmentCount);
    }
  }

  applyAIGuidedTransformation(img, keypoints, aiParams, weightLossKg) {
    console.log('🤖 Applying AI-guided transformation:', aiParams);
    
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d', { 
      willReadFrequently: true,
      alpha: false
    });
    
    const maxCanvasSize = 2048;
    canvas.width = Math.min(img.width, maxCanvasSize);
    canvas.height = Math.min(img.height, maxCanvasSize);

    try {
      // Extract body info from keypoints
      const bodyInfo = this.extractBodyInfo(keypoints, img.width, img.height);
      
      if (bodyInfo.isValid) {
        // Apply AI-optimized transformation with specific parameters for each area
        this.applyAIOptimizedSlimming(ctx, img, bodyInfo, aiParams, weightLossKg);
        
        // Apply smoothing based on AI recommendations
        const smoothingRadius = aiParams.recommended_adjustments?.smoothing_radius || 15;
        this.applyAdvancedSmoothing(ctx, bodyInfo, smoothingRadius);
        
        // Add AI indicator
        const treatmentCount = aiParams.treatments || 30;
        const displayText = weightLossKg ? `${weightLossKg} kg (AI-Enhanced)` : `AI-Enhanced + ${treatmentCount} Behandlungen`;
        this.addIndicator(ctx, displayText, 0, '#00ff88');
      } else {
        return this.createSmartTransformation(img, 20, 30, weightLossKg);
      }

      const result = canvas.toDataURL('image/jpeg', 0.88);
      
      // Cleanup
      canvas.width = 1;
      canvas.height = 1;
      
      return result;
      
    } catch (error) {
      console.error('Error in AI-guided transformation:', error);
      canvas.width = 1;
      canvas.height = 1;
      throw error;
    }
  }

  applyAIOptimizedSlimming(ctx, img, bodyInfo, aiParams, weightLossKg) {
    console.log('🎨 Starting Premium AI Transformation with all enhancements...');
    
    // STEP 1: Extract and preserve texture details
    const textureData = this.extractTextureDetails(ctx, img, bodyInfo);
    console.log('✅ Texture details extracted');
    
    // STEP 2: Analyze lighting and shadows
    const lightingData = this.analyzeLighting(ctx, img, bodyInfo);
    console.log('✅ Lighting analyzed');
    
    // Draw original image
    ctx.drawImage(img, 0, 0, img.width, img.height);
    
    const imageData = ctx.getImageData(0, 0, img.width, img.height);
    const pixels = imageData.data;
    const width = img.width;
    const height = img.height;
    
    const centerX = bodyInfo.centerX;
    const bodyTop = bodyInfo.topY;
    const bodyBottom = bodyInfo.bottomY;
    const bodyLeft = bodyInfo.leftX;
    const bodyRight = bodyInfo.rightX;
    
    // Get AI parameters with defaults
    const waistReduction = (aiParams.waist_reduction || 20) / 100;
    const abdomenReduction = (aiParams.abdomen_reduction || 18) / 100;
    const hipReduction = (aiParams.hip_reduction || 12) / 100;
    const intensity = aiParams.transformation_intensity || 0.7;
    
    console.log(`🎯 AI Parameters: Waist=${waistReduction*100}%, Abdomen=${abdomenReduction*100}%, Intensity=${intensity}`);
    
    // STEP 3: Multi-scale decomposition
    const scales = this.decomposeMultiScale(imageData, 3);
    console.log('✅ Multi-scale decomposition complete (3 scales)');
    
    // Create new image data
    const newImageData = ctx.createImageData(width, height);
    const newPixels = newImageData.data;
    
    // Copy all pixels first
    for (let i = 0; i < pixels.length; i++) {
      newPixels[i] = pixels[i];
    }
    
    // Define body regions based on keypoints - EXCLUDING FEET AND LOWER LEGS
    const waistY = bodyTop + (bodyBottom - bodyTop) * 0.5;
    const abdomenY = bodyTop + (bodyBottom - bodyTop) * 0.6;
    const hipY = bodyTop + (bodyBottom - bodyTop) * 0.7;
    const kneeY = bodyTop + (bodyBottom - bodyTop) * 0.85; // NEW: Knee level
    const transformBottom = kneeY; // Stop transformation at knees, NOT at feet
    
    console.log(`🦵 Transformation boundaries: Top=${bodyTop}, Waist=${waistY}, Knee=${kneeY}, ActualBottom=${bodyBottom} (feet excluded)`);
    
    // STEP 4: Edge-aware transformation map
    const edgeMap = this.createEdgeMap(pixels, width, height, bodyInfo);
    console.log('✅ Edge map created');
    
    // STEP 5: Apply transformation with all enhancements - STOP AT KNEES
    for (let y = bodyTop; y < transformBottom; y++) {  // Changed from bodyBottom to transformBottom
      for (let x = bodyLeft; x < bodyRight; x++) {
        const distFromCenter = Math.abs(x - centerX);
        const bodyWidth = bodyRight - bodyLeft;
        
        // Determine which region and apply appropriate reduction
        let reductionFactor = 0;
        let verticalOffset = 0;
        
        if (y >= bodyTop && y < waistY) {
          reductionFactor = waistReduction * 0.7;
          verticalOffset = -1;
        } else if (y >= waistY && y < abdomenY) {
          const waistProgress = (y - waistY) / (abdomenY - waistY);
          reductionFactor = waistReduction * (1.0 - waistProgress * 0.3);
          verticalOffset = -2;
        } else if (y >= abdomenY && y < hipY) {
          const abdomenProgress = (y - abdomenY) / (hipY - abdomenY);
          reductionFactor = abdomenReduction * (1.0 - abdomenProgress * 0.4);
          verticalOffset = -1.5;
        } else if (y >= hipY && y < kneeY) {
          // Thighs - with GRADUAL FADEOUT towards knees
          const thighProgress = (y - hipY) / (kneeY - hipY);
          reductionFactor = hipReduction * (1.0 - thighProgress * 0.8); // Stronger fadeout
          verticalOffset = -0.5 * (1.0 - thighProgress); // Reduce vertical shift near knees
        }
        
        reductionFactor *= intensity;
        
        // Additional fadeout near knee boundary to prevent artifacts
        if (y > kneeY - 20) {
          const fadeDistance = (kneeY - y) / 20; // 0 at knee, 1 at knee-20
          reductionFactor *= fadeDistance;
          verticalOffset *= fadeDistance;
        }
        
        // Edge-aware strength modulation
        const edgeStrength = edgeMap[(y * width + x)];
        const edgeModulation = 1.0 - (edgeStrength * 0.3);
        
        const normalizedDist = distFromCenter / (bodyWidth / 2);
        const transformStrength = Math.pow(1 - normalizedDist, 3) * reductionFactor * edgeModulation;
        
        if (transformStrength > 0.01) {
          const pullDistance = distFromCenter * transformStrength;
          const sourceX = x < centerX ? x + pullDistance : x - pullDistance;
          const sourceY = y + (verticalOffset * transformStrength * intensity);
          
          // Advanced 2D interpolation with texture preservation
          if (sourceX >= 0 && sourceX < width && sourceY >= 0 && sourceY < height) {
            const srcX = Math.floor(sourceX);
            const srcX1 = Math.min(srcX + 1, width - 1);
            const srcY = Math.floor(sourceY);
            const srcY1 = Math.min(srcY + 1, height - 1);
            
            const fracX = sourceX - srcX;
            const fracY = sourceY - srcY;
            
            const dstIdx = (y * width + x) * 4;
            
            // Sample and interpolate
            for (let c = 0; c < 3; c++) {
              const idx00 = (srcY * width + srcX) * 4 + c;
              const idx10 = (srcY * width + srcX1) * 4 + c;
              const idx01 = (srcY1 * width + srcX) * 4 + c;
              const idx11 = (srcY1 * width + srcX1) * 4 + c;
              
              const val0 = pixels[idx00] * (1 - fracX) + pixels[idx10] * fracX;
              const val1 = pixels[idx01] * (1 - fracX) + pixels[idx11] * fracX;
              const baseColor = val0 * (1 - fracY) + val1 * fracY;
              
              // Re-apply texture details (high-frequency preservation)
              const textureDetail = textureData[(y * width + x) * 4 + c];
              newPixels[dstIdx + c] = Math.max(0, Math.min(255, baseColor + textureDetail * 0.7));
            }
            newPixels[dstIdx + 3] = 255;
          }
        }
      }
    }
    
    ctx.putImageData(newImageData, 0, 0);
    console.log('✅ Transformation applied (feet and lower legs preserved)');
    
    // STEP 6: Apply lighting adjustments
    this.adjustLightingAndShadows(ctx, bodyInfo, lightingData, intensity);
    console.log('✅ Lighting and shadows adjusted');
    
    // STEP 7: Skin tone consistency
    this.applySkinToneConsistency(ctx, bodyInfo);
    console.log('✅ Skin tone consistency applied');
    
    // STEP 8: Clothing adaptation
    this.adaptClothingContours(ctx, bodyInfo, waistReduction, intensity);
    console.log('✅ Clothing contours adapted');
    
    // STEP 9: Skin tightening
    this.applySkinTighteningEffect(ctx, bodyInfo, intensity);
    console.log('✅ Skin tightening effect applied');
    
    // STEP 10: Muscle tone
    this.applyMuscleToneEnhancement(ctx, bodyInfo, intensity);
    console.log('✅ Muscle tone enhancement applied');
    
    console.log('🎉 Premium transformation complete - Feet preserved!');
  }

  // NEW: Extract high-frequency texture details
  extractTextureDetails(ctx, img, bodyInfo) {
    const imageData = ctx.getImageData(0, 0, img.width, img.height);
    const pixels = imageData.data;
    const width = img.width;
    const height = img.height;
    const textureData = new Float32Array(pixels.length);
    
    // Apply high-pass filter to extract details
    for (let y = 1; y < height - 1; y++) {
      for (let x = 1; x < width - 1; x++) {
        const idx = (y * width + x) * 4;
        
        for (let c = 0; c < 3; c++) {
          const center = pixels[idx + c];
          const avg = (
            pixels[((y-1) * width + x) * 4 + c] +
            pixels[((y+1) * width + x) * 4 + c] +
            pixels[(y * width + (x-1)) * 4 + c] +
            pixels[(y * width + (x+1)) * 4 + c]
          ) / 4;
          
          // High-frequency component (texture)
          textureData[idx + c] = center - avg;
        }
      }
    }
    
    return textureData;
  }

  // NEW: Analyze lighting patterns
  analyzeLighting(ctx, img, bodyInfo) {
    const imageData = ctx.getImageData(0, 0, img.width, img.height);
    const pixels = imageData.data;
    const width = img.width;
    
    const lightingData = {
      highlights: [],
      shadows: [],
      averageBrightness: 0
    };
    
    let totalBrightness = 0;
    let count = 0;
    
    for (let y = bodyInfo.topY; y < bodyInfo.bottomY; y++) {
      for (let x = bodyInfo.leftX; x < bodyInfo.rightX; x++) {
        const idx = (y * width + x) * 4;
        const brightness = (pixels[idx] + pixels[idx + 1] + pixels[idx + 2]) / 3;
        
        totalBrightness += brightness;
        count++;
        
        // Detect highlights (bright areas)
        if (brightness > 200) {
          lightingData.highlights.push({ x, y, value: brightness });
        }
        
        // Detect shadows (dark areas)
        if (brightness < 80) {
          lightingData.shadows.push({ x, y, value: brightness });
        }
      }
    }
    
    lightingData.averageBrightness = totalBrightness / count;
    return lightingData;
  }

  // NEW: Multi-scale decomposition
  decomposeMultiScale(imageData, levels) {
    const scales = [];
    let currentScale = imageData;
    
    for (let i = 0; i < levels; i++) {
      const downsampled = this.downsampleImageData(currentScale);
      scales.push(downsampled);
      currentScale = downsampled;
    }
    
    return scales;
  }

  downsampleImageData(imageData) {
    const width = Math.floor(imageData.width / 2);
    const height = Math.floor(imageData.height / 2);
    const downsampled = new ImageData(width, height);
    
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const srcX = x * 2;
        const srcY = y * 2;
        const srcIdx = (srcY * imageData.width + srcX) * 4;
        const dstIdx = (y * width + x) * 4;
        
        for (let c = 0; c < 4; c++) {
          downsampled.data[dstIdx + c] = imageData.data[srcIdx + c];
        }
      }
    }
    
    return downsampled;
  }

  // NEW: Create edge detection map
  createEdgeMap(pixels, width, height, bodyInfo) {
    const edgeMap = new Float32Array(width * height);
    
    // Sobel edge detection
    for (let y = bodyInfo.topY + 1; y < bodyInfo.bottomY - 1; y++) {
      for (let x = bodyInfo.leftX + 1; x < bodyInfo.rightX - 1; x++) {
        const idx = y * width + x;
        
        // Calculate gradient
        let gx = 0, gy = 0;
        
        for (let c = 0; c < 3; c++) {
          // Horizontal gradient
          gx += -pixels[((y-1) * width + (x-1)) * 4 + c] 
                -2 * pixels[(y * width + (x-1)) * 4 + c]
                -pixels[((y+1) * width + (x-1)) * 4 + c]
                +pixels[((y-1) * width + (x+1)) * 4 + c]
                +2 * pixels[(y * width + (x+1)) * 4 + c]
                +pixels[((y+1) * width + (x+1)) * 4 + c];
          
          // Vertical gradient
          gy += -pixels[((y-1) * width + (x-1)) * 4 + c]
                -2 * pixels[((y-1) * width + x) * 4 + c]
                -pixels[((y-1) * width + (x+1)) * 4 + c]
                +pixels[((y+1) * width + (x-1)) * 4 + c]
                +2 * pixels[((y+1) * width + x) * 4 + c]
                +pixels[((y+1) * width + (x+1)) * 4 + c];
        }
        
        // Edge strength
        edgeMap[idx] = Math.sqrt(gx * gx + gy * gy) / (255 * 3 * 8);
      }
    }
    
    return edgeMap;
  }

  // NEW: Adjust lighting and shadows after transformation
  adjustLightingAndShadows(ctx, bodyInfo, lightingData, intensity) {
    const imageData = ctx.getImageData(0, 0, ctx.canvas.width, ctx.canvas.height);
    const pixels = imageData.data;
    const width = imageData.width;
    
    const centerX = bodyInfo.centerX;
    const waistY = bodyInfo.topY + (bodyInfo.bottomY - bodyInfo.topY) * 0.5;
    const abdomenY = bodyInfo.topY + (bodyInfo.bottomY - bodyInfo.topY) * 0.65;
    
    for (let y = waistY; y < abdomenY; y++) {
      for (let x = bodyInfo.leftX; x < bodyInfo.rightX; x++) {
        const idx = (y * width + x) * 4;
        const distFromCenter = Math.abs(x - centerX);
        const bodyWidth = bodyInfo.rightX - bodyInfo.leftX;
        const normalizedDist = distFromCenter / (bodyWidth / 2);
        
        if (normalizedDist < 0.7) {
          // Adjust lighting based on new contours
          // Add highlight to center (slimmer area appears more forward)
          const lightingAdjust = (1 - normalizedDist) * intensity * 0.15;
          
          for (let c = 0; c < 3; c++) {
            pixels[idx + c] = Math.min(255, pixels[idx + c] * (1 + lightingAdjust));
          }
        } else if (normalizedDist > 0.7) {
          // Darken edges slightly (side contours)
          const shadowAdjust = (normalizedDist - 0.7) * intensity * 0.1;
          
          for (let c = 0; c < 3; c++) {
            pixels[idx + c] = Math.max(0, pixels[idx + c] * (1 - shadowAdjust));
          }
        }
      }
    }
    
    ctx.putImageData(imageData, 0, 0);
  }

  // NEW: Maintain consistent skin tone
  applySkinToneConsistency(ctx, bodyInfo) {
    const imageData = ctx.getImageData(0, 0, ctx.canvas.width, ctx.canvas.height);
    const pixels = imageData.data;
    const width = imageData.width;
    
    // Calculate average skin tone from untransformed areas (face/arms)
    let avgR = 0, avgG = 0, avgB = 0, count = 0;
    
    const sampleY = bodyInfo.topY - 20; // Above body (face area)
    if (sampleY > 0) {
      for (let x = bodyInfo.leftX; x < bodyInfo.rightX; x++) {
        const idx = (sampleY * width + x) * 4;
        avgR += pixels[idx];
        avgG += pixels[idx + 1];
        avgB += pixels[idx + 2];
        count++;
      }
    }
    
    if (count > 0) {
      avgR /= count;
      avgG /= count;
      avgB /= count;
      
      // Apply color correction to transformed body area
      for (let y = bodyInfo.topY; y < bodyInfo.bottomY; y++) {
        for (let x = bodyInfo.leftX; x < bodyInfo.rightX; x++) {
          const idx = (y * width + x) * 4;
          
          const currentR = pixels[idx];
          const currentG = pixels[idx + 1];
          const currentB = pixels[idx + 2];
          
          // Subtle color shift towards reference skin tone
          const colorShift = 0.1;
          pixels[idx] = currentR * (1 - colorShift) + avgR * colorShift;
          pixels[idx + 1] = currentG * (1 - colorShift) + avgG * colorShift;
          pixels[idx + 2] = currentB * (1 - colorShift) + avgB * colorShift;
        }
      }
    }
    
    ctx.putImageData(imageData, 0, 0);
  }

  // NEW: Adapt clothing contours to new body shape
  adaptClothingContours(ctx, bodyInfo, reductionAmount, intensity) {
    const imageData = ctx.getImageData(0, 0, ctx.canvas.width, ctx.canvas.height);
    const pixels = imageData.data;
    const width = imageData.width;
    
    const centerX = bodyInfo.centerX;
    const waistY = bodyInfo.topY + (bodyInfo.bottomY - bodyInfo.topY) * 0.5;
    const hipY = bodyInfo.topY + (bodyInfo.bottomY - bodyInfo.topY) * 0.7;
    
    // Detect potential clothing wrinkles/folds (high contrast areas)
    for (let y = waistY; y < hipY; y++) {
      for (let x = bodyInfo.leftX + 10; x < bodyInfo.rightX - 10; x++) {
        const idx = (y * width + x) * 4;
        const leftIdx = (y * width + (x - 5)) * 4;
        const rightIdx = (y * width + (x + 5)) * 4;
        
        // Detect horizontal edges (potential folds)
        const contrast = Math.abs(pixels[idx] - pixels[leftIdx]) + 
                        Math.abs(pixels[idx] - pixels[rightIdx]);
        
        if (contrast > 60) { // High contrast = potential fold
          const distFromCenter = Math.abs(x - centerX);
          const bodyWidth = bodyInfo.rightX - bodyInfo.leftX;
          const normalizedDist = distFromCenter / (bodyWidth / 2);
          
          // Adjust fold intensity based on transformation
          const foldAdjust = reductionAmount * intensity * (1 - normalizedDist) * 0.08;
          
          for (let c = 0; c < 3; c++) {
            // Slightly darken folds to emphasize adapted contours
            pixels[idx + c] = Math.max(0, pixels[idx + c] * (1 - foldAdjust));
          }
        }
      }
    }
    
    ctx.putImageData(imageData, 0, 0);
  }

  applySkinTighteningEffect(ctx, bodyInfo, intensity) {
    // Simulate skin tightening through subtle brightness/contrast adjustments
    const imageData = ctx.getImageData(0, 0, ctx.canvas.width, ctx.canvas.height);
    const pixels = imageData.data;
    const width = imageData.width;
    
    const centerX = bodyInfo.centerX;
    const bodyTop = bodyInfo.topY;
    const bodyBottom = bodyInfo.bottomY;
    const bodyLeft = bodyInfo.leftX;
    const bodyRight = bodyInfo.rightX;
    
    // Define tightening zones
    const waistY = bodyTop + (bodyBottom - bodyTop) * 0.5;
    const abdomenY = bodyTop + (bodyBottom - bodyTop) * 0.65;
    
    for (let y = waistY; y < abdomenY; y++) {
      for (let x = bodyLeft; x < bodyRight; x++) {
        const idx = (y * width + x) * 4;
        
        // Calculate distance from center for natural falloff
        const distFromCenter = Math.abs(x - centerX);
        const bodyWidth = bodyRight - bodyLeft;
        const normalizedDist = distFromCenter / (bodyWidth / 2);
        
        if (normalizedDist < 0.8) { // Only affect inner body areas
          // Increase brightness slightly for "tighter" appearance
          const tighteningFactor = (1 - normalizedDist) * intensity * 0.08;
          
          for (let c = 0; c < 3; c++) {
            // Subtle brightening for skin tightening effect
            pixels[idx + c] = Math.min(255, pixels[idx + c] * (1 + tighteningFactor));
          }
        }
      }
    }
    
    ctx.putImageData(imageData, 0, 0);
    console.log('✅ Skin tightening effect applied');
  }

  applyMuscleToneEnhancement(ctx, bodyInfo, intensity) {
    // Enhance muscle definition through subtle contrast adjustment
    const imageData = ctx.getImageData(0, 0, ctx.canvas.width, ctx.canvas.height);
    const pixels = imageData.data;
    const width = imageData.width;
    
    const centerX = bodyInfo.centerX;
    const bodyTop = bodyInfo.topY;
    const bodyBottom = bodyInfo.bottomY;
    const bodyLeft = bodyInfo.leftX;
    const bodyRight = bodyInfo.rightX;
    
    // Focus on arms and abdomen for muscle tone
    const abdomenTop = bodyTop + (bodyBottom - bodyTop) * 0.4;
    const abdomenBottom = bodyTop + (bodyBottom - bodyTop) * 0.7;
    
    for (let y = abdomenTop; y < abdomenBottom; y++) {
      for (let x = bodyLeft; x < bodyRight; x++) {
        const idx = (y * width + x) * 4;
        
        const distFromCenter = Math.abs(x - centerX);
        const bodyWidth = bodyRight - bodyLeft;
        const normalizedDist = distFromCenter / (bodyWidth / 2);
        
        // Apply subtle contrast enhancement for muscle definition
        if (normalizedDist > 0.3 && normalizedDist < 0.7) {
          const toneFactor = (1 - Math.abs(normalizedDist - 0.5) * 2) * intensity * 0.12;
          
          for (let c = 0; c < 3; c++) {
            // Increase contrast for muscle tone appearance
            const centered = pixels[idx + c] - 128;
            pixels[idx + c] = Math.max(0, Math.min(255, 128 + centered * (1 + toneFactor)));
          }
        }
      }
    }
    
    ctx.putImageData(imageData, 0, 0);
    console.log('✅ Muscle tone enhancement applied');
  }

  applyAdvancedSmoothing(ctx, bodyInfo, radius) {
    // Apply multi-pass gaussian blur for ultra-smooth edges
    const imageData = ctx.getImageData(0, 0, ctx.canvas.width, ctx.canvas.height);
    
    // Apply smoothing in multiple passes for better quality
    let smoothedData = imageData;
    for (let pass = 0; pass < 2; pass++) {
      smoothedData = this.gaussianBlur(smoothedData, radius, bodyInfo);
    }
    
    ctx.putImageData(smoothedData, 0, 0);
    console.log(`✅ Advanced multi-pass smoothing applied (radius: ${radius}px, 2 passes)`);
  }

  gaussianBlur(imageData, radius, bodyInfo) {
    // Enhanced gaussian blur with better edge handling
    const pixels = imageData.data;
    const width = imageData.width;
    const height = imageData.height;
    const output = new Uint8ClampedArray(pixels);
    
    const bodyLeft = bodyInfo.leftX;
    const bodyRight = bodyInfo.rightX;
    const bodyTop = bodyInfo.topY;
    const bodyBottom = bodyInfo.bottomY;
    
    // Blur edges and transition zones more aggressively
    const blurMargin = Math.floor(radius * 2.5);
    const blurRadius = Math.floor(radius / 2);
    
    // Left edge
    for (let y = bodyTop; y < bodyBottom; y++) {
      for (let x = Math.max(0, bodyLeft - blurMargin); x < Math.min(width, bodyLeft + blurMargin); x++) {
        this.blurPixelWeighted(pixels, output, x, y, width, height, blurRadius);
      }
    }
    
    // Right edge
    for (let y = bodyTop; y < bodyBottom; y++) {
      for (let x = Math.max(0, bodyRight - blurMargin); x < Math.min(width, bodyRight + blurMargin); x++) {
        this.blurPixelWeighted(pixels, output, x, y, width, height, blurRadius);
      }
    }
    
    // Top transition
    for (let y = Math.max(0, bodyTop - 10); y < Math.min(height, bodyTop + 20); y++) {
      for (let x = bodyLeft; x < bodyRight; x++) {
        this.blurPixelWeighted(pixels, output, x, y, width, height, Math.floor(blurRadius * 0.8));
      }
    }
    
    // Bottom transition
    for (let y = Math.max(0, bodyBottom - 20); y < Math.min(height, bodyBottom + 10); y++) {
      for (let x = bodyLeft; x < bodyRight; x++) {
        this.blurPixelWeighted(pixels, output, x, y, width, height, Math.floor(blurRadius * 0.8));
      }
    }
    
    imageData.data.set(output);
    return imageData;
  }

  blurPixelWeighted(input, output, x, y, width, height, radius) {
    let r = 0, g = 0, b = 0, totalWeight = 0;
    
    // Gaussian weights for better blur quality
    const sigma = radius / 2;
    
    for (let dy = -radius; dy <= radius; dy++) {
      for (let dx = -radius; dx <= radius; dx++) {
        const nx = x + dx;
        const ny = y + dy;
        
        if (nx >= 0 && nx < width && ny >= 0 && ny < height) {
          // Calculate gaussian weight
          const distance = Math.sqrt(dx * dx + dy * dy);
          const weight = Math.exp(-(distance * distance) / (2 * sigma * sigma));
          
          const idx = (ny * width + nx) * 4;
          r += input[idx] * weight;
          g += input[idx + 1] * weight;
          b += input[idx + 2] * weight;
          totalWeight += weight;
        }
      }
    }
    
    if (totalWeight > 0) {
      const idx = (y * width + x) * 4;
      output[idx] = r / totalWeight;
      output[idx + 1] = g / totalWeight;
      output[idx + 2] = b / totalWeight;
    }
  }

  resizeImage(img, maxDimension) {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      let width = img.width;
      let height = img.height;
      
      // Calculate new dimensions while maintaining aspect ratio
      if (width > height) {
        if (width > maxDimension) {
          height = (height * maxDimension) / width;
          width = maxDimension;
        }
      } else {
        if (height > maxDimension) {
          width = (width * maxDimension) / height;
          height = maxDimension;
        }
      }
      
      canvas.width = width;
      canvas.height = height;
      
      // Use high-quality image smoothing
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      
      ctx.drawImage(img, 0, 0, width, height);
      
      // Convert canvas to image
      const resizedImg = new Image();
      resizedImg.onload = () => resolve(resizedImg);
      resizedImg.src = canvas.toDataURL('image/jpeg', 0.92);
    });
  }

  loadImage(src) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = (e) => reject(new Error(`Failed to load image: ${e}`));
      img.src = src;
    });
  }

  applyMoveNetSlimming(img, keypoints, weightLossPercentage, treatmentCount, weightLossKg) {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d', { 
      willReadFrequently: true,
      alpha: false // Optimize for opaque images
    });
    
    // Limit canvas size for mobile devices
    const maxCanvasSize = 2048;
    canvas.width = Math.min(img.width, maxCanvasSize);
    canvas.height = Math.min(img.height, maxCanvasSize);

    try {
      // Extract body center and dimensions from MoveNet keypoints
      const bodyInfo = this.extractBodyInfo(keypoints, img.width, img.height);
      
      if (bodyInfo.isValid) {
        // Apply targeted slimming transformation with kg-based scaling
        this.applyTargetedSlimming(ctx, img, bodyInfo, weightLossPercentage, weightLossKg);
        
        // Apply skin tightening based on treatment count
        this.applySkinTightening(ctx, img, bodyInfo, treatmentCount);
        
        // Apply abdominal muscle enhancement based on treatment count
        this.applyAbdominalMuscleEnhancement(ctx, bodyInfo, treatmentCount);
        
        // Add success indicator with treatment info and kg loss
        const displayText = weightLossKg ? `${weightLossKg} kg + ${treatmentCount} Behandlungen` : `MoveNet + ${treatmentCount} Behandlungen`;
        this.addIndicator(ctx, displayText, weightLossPercentage, '#00ff88');
      } else {
        // Fallback if keypoints are insufficient
        return this.createSmartTransformation(img, weightLossPercentage, treatmentCount, weightLossKg);
      }

      const result = canvas.toDataURL('image/jpeg', 0.85);
      
      // MEMORY CLEANUP: Clear canvas after conversion
      canvas.width = 1;
      canvas.height = 1;
      
      return result;
      
    } catch (error) {
      console.error('Error in applyMoveNetSlimming:', error);
      // Cleanup on error
      canvas.width = 1;
      canvas.height = 1;
      throw error;
    }
  }

  extractBodyInfo(keypoints, width, height) {
    try {
      // MoveNet keypoint names (17 keypoints total)
      const keypointNames = [
        'nose', 'left_eye', 'right_eye', 'left_ear', 'right_ear',
        'left_shoulder', 'right_shoulder', 'left_elbow', 'right_elbow',
        'left_wrist', 'right_wrist', 'left_hip', 'right_hip',
        'left_knee', 'right_knee', 'left_ankle', 'right_ankle'
      ];

      // Find key body parts (shoulders and hips)
      const leftShoulder = keypoints[5]; // left_shoulder
      const rightShoulder = keypoints[6]; // right_shoulder
      const leftHip = keypoints[11]; // left_hip
      const rightHip = keypoints[12]; // right_hip

      // Check if we have reliable body keypoints (confidence > 0.3)
      const validKeypoints = [leftShoulder, rightShoulder, leftHip, rightHip]
        .filter(kp => kp && kp.score > 0.3);

      if (validKeypoints.length >= 3) {
        // Calculate body center and dimensions
        const shouldersX = (leftShoulder.x + rightShoulder.x) / 2;
        const shouldersY = (leftShoulder.y + rightShoulder.y) / 2;
        const hipsX = (leftHip.x + rightHip.x) / 2;
        const hipsY = (leftHip.y + rightHip.y) / 2;

        return {
          isValid: true,
          centerX: (shouldersX + hipsX) / 2,
          centerY: (shouldersY + hipsY) / 2,
          shouldersX: shouldersX,
          shouldersY: shouldersY,
          hipsX: hipsX, 
          hipsY: hipsY,
          shouldersWidth: Math.abs(rightShoulder.x - leftShoulder.x),
          hipsWidth: Math.abs(rightHip.x - leftHip.x),
          torsoTop: shouldersY,
          torsoBottom: hipsY,
          leftBound: Math.min(leftShoulder.x, leftHip.x) - 0.05,
          rightBound: Math.max(rightShoulder.x, rightHip.x) + 0.05
        };
      }

      return { isValid: false };

    } catch (error) {
      console.error('Error extracting body info:', error);
      return { isValid: false };
    }
  }

  applyTargetedSlimming(ctx, img, bodyInfo, weightLossPercentage, weightLossKg = null) {
    // Draw original image as base
    ctx.drawImage(img, 0, 0);
    
    // Calculate transformation strength - INCREASED for visible effect
    let baseTransformation;
    if (weightLossKg && weightLossKg > 0) {
      baseTransformation = Math.min(weightLossKg * 0.5, 12.0); // DOUBLED from 0.25
      console.log(`✅ Slimming strength: ${baseTransformation.toFixed(2)} (from ${weightLossKg}kg loss)`);
    } else {
      baseTransformation = Math.min(weightLossPercentage * 0.3, 6.0); // DOUBLED from 0.15
    }
    
    // Get image data
    const imageData = ctx.getImageData(0, 0, img.width, img.height);
    const newImageData = ctx.createImageData(img.width, img.height);
    const srcData = imageData.data;
    const dstData = newImageData.data;
    
    // Define body bounds
    const bodyCenterX = bodyInfo.centerX * img.width;
    const shouldersY = bodyInfo.shouldersY * img.height;
    const hipsY = bodyInfo.hipsY * img.height;
    const bodyTop = shouldersY - (hipsY - shouldersY) * 0.15;
    const bodyBottom = hipsY + (hipsY - shouldersY) * 0.8;
    
    // Calculate body width at different heights for better masking
    const shouldersWidth = bodyInfo.shouldersWidth * img.width;
    const hipsWidth = bodyInfo.hipsWidth * img.width;
    
    console.log(`🎯 Body transformation zone: Center=${bodyCenterX.toFixed(0)}, Y=${bodyTop.toFixed(0)}-${bodyBottom.toFixed(0)}`);
    
    // Create body mask based on keypoints
    const bodyMask = this.createBodyMask(img.width, img.height, bodyCenterX, shouldersY, hipsY, shouldersWidth, hipsWidth);
    
    // Process each pixel with STRICT body masking
    for (let y = 0; y < img.height; y++) {
      for (let x = 0; x < img.width; x++) {
        const pixelIndex = (y * img.width + x) * 4;
        let sourceX = x;
        
        // CHECK MASK FIRST - if not on body, copy directly (NO transformation)
        const maskValue = bodyMask[y * img.width + x];
        
        if (maskValue > 0.05) {
          // This pixel is on the body - apply transformation
          
          // Calculate vertical position
          const relativeY = (y - bodyTop) / (bodyBottom - bodyTop);
          
          // EXTREME multipliers for visible shoulder narrowing
          let zoneMultiplier = 1.0;
          
          if (relativeY < 0.15) {
            // Head - no transform
            zoneMultiplier = 0.0;
          } else if (relativeY < 0.25) {
            // Neck - ramp up FAST
            const t = (relativeY - 0.15) / 0.1;
            zoneMultiplier = 2.5 * Math.pow(t, 2); // EXTREME ramp to 2.5
          } else if (relativeY < 0.45) {
            // SHOULDERS & CHEST - EXTREME narrowing
            zoneMultiplier = 2.5; // VERY HIGH for very narrow shoulders
          } else if (relativeY < 0.60) {
            // Transition to waist
            const t = (relativeY - 0.45) / 0.15;
            zoneMultiplier = 2.5 - 1.8 * Math.pow(t, 2); // 2.5 → 0.7
          } else if (relativeY < 0.75) {
            // WAIST - minimal transformation
            zoneMultiplier = 0.7;
          } else if (relativeY < 0.85) {
            // HIPS
            zoneMultiplier = 0.6;
          } else if (relativeY < 0.95) {
            // LEGS - fade out
            const t = (relativeY - 0.85) / 0.1;
            zoneMultiplier = 0.6 * (1.0 - Math.pow(t, 2));
          } else {
            // Feet - no transform
            zoneMultiplier = 0.0;
          }
          
          // Apply mask intensity
          const effectiveTransform = baseTransformation * zoneMultiplier * maskValue;
          
          if (effectiveTransform > 0.01) {
            // INCREASED compression for dramatic effect
            const offsetFromCenter = x - bodyCenterX;
            const compressionFactor = effectiveTransform * 0.15; // INCREASED from 0.10
            const newOffset = offsetFromCenter * (1 - compressionFactor);
            sourceX = bodyCenterX + newOffset;
            sourceX = Math.max(0, Math.min(img.width - 1, Math.round(sourceX)));
          }
        }
        // else: maskValue <= 0.05 means BACKGROUND - sourceX stays at x (no transformation)
        
        // Copy pixel from source location
        const sourceIndex = (y * img.width + sourceX) * 4;
        dstData[pixelIndex] = srcData[sourceIndex];         // R
        dstData[pixelIndex + 1] = srcData[sourceIndex + 1]; // G
        dstData[pixelIndex + 2] = srcData[sourceIndex + 2]; // B
        dstData[pixelIndex + 3] = srcData[sourceIndex + 3]; // A
      }
    }
    
    // Apply transformed image
    ctx.putImageData(newImageData, 0, 0);
    
    console.log('✅ Body-masked slimming complete (background untouched, shoulders narrowed)');
    
    // Apply additional body-only effects
    this.applyBellyFlatteningEffect(ctx, img, bodyInfo, weightLossPercentage, treatmentCount, weightLossKg);
    this.applyBellyLiftingEffect(ctx, img, bodyInfo, weightLossPercentage, treatmentCount, weightLossKg);
  }
  
  // Create ultra-smooth body mask with Gaussian blur
  createBodyMask(width, height, centerX, shouldersY, hipsY, shouldersWidth, hipsWidth) {
    const mask = new Float32Array(width * height);
    
    // Extend body region with extra space
    const neckToShoulderDist = (hipsY - shouldersY) * 0.25;
    const bodyTop = shouldersY - neckToShoulderDist * 2.0;
    const bodyBottom = hipsY + (hipsY - shouldersY) * 1.5;
    const bodyHeight = bodyBottom - bodyTop;
    
    // Create initial mask
    for (let y = 0; y < height; y++) {
      if (y < bodyTop || y > bodyBottom) {
        continue;
      }
      
      const relativeY = (y - bodyTop) / bodyHeight;
      let bodyWidthAtY;
      let smoothingFactor = 1.0;
      
      // Smooth width progression
      if (relativeY < 0.25) {
        bodyWidthAtY = shouldersWidth * (0.5 + 0.5 * relativeY / 0.25);
        smoothingFactor = relativeY / 0.25;
      } else if (relativeY < 0.65) {
        const t = (relativeY - 0.25) / 0.4;
        bodyWidthAtY = shouldersWidth + (hipsWidth - shouldersWidth) * t;
        smoothingFactor = 1.0;
      } else if (relativeY < 0.92) {
        bodyWidthAtY = hipsWidth;
        smoothingFactor = 1.0;
      } else {
        const t = (relativeY - 0.92) / 0.08;
        bodyWidthAtY = hipsWidth * 0.5;
        smoothingFactor = 1.0 - t;
      }
      
      const bodyRadius = bodyWidthAtY * 0.7;
      
      for (let x = 0; x < width; x++) {
        const distanceFromCenter = Math.abs(x - centerX);
        
        if (distanceFromCenter < bodyRadius) {
          const normalizedDist = distanceFromCenter / bodyRadius;
          
          if (normalizedDist < 0.5) {
            mask[y * width + x] = smoothingFactor;
          } else {
            const edgeT = (normalizedDist - 0.5) / 0.5;
            mask[y * width + x] = smoothingFactor * (1.0 - edgeT * edgeT);
          }
        }
      }
    }
    
    // Apply Gaussian blur to mask for ultra-smooth transitions
    const blurredMask = this.gaussianBlurMask(mask, width, height, 15); // 15px blur radius
    
    console.log('✅ Ultra-smooth blurred body mask created');
    return blurredMask;
  }
  
  // Apply Gaussian blur to mask for seamless transitions
  gaussianBlurMask(mask, width, height, radius) {
    const blurred = new Float32Array(width * height);
    const kernel = this.createGaussianKernel(radius);
    const kernelSize = kernel.length;
    const halfKernel = Math.floor(kernelSize / 2);
    
    // Horizontal blur
    const temp = new Float32Array(width * height);
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        let sum = 0;
        let weightSum = 0;
        
        for (let k = 0; k < kernelSize; k++) {
          const xk = x + k - halfKernel;
          if (xk >= 0 && xk < width) {
            sum += mask[y * width + xk] * kernel[k];
            weightSum += kernel[k];
          }
        }
        temp[y * width + x] = weightSum > 0 ? sum / weightSum : 0;
      }
    }
    
    // Vertical blur
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        let sum = 0;
        let weightSum = 0;
        
        for (let k = 0; k < kernelSize; k++) {
          const yk = y + k - halfKernel;
          if (yk >= 0 && yk < height) {
            sum += temp[yk * width + x] * kernel[k];
            weightSum += kernel[k];
          }
        }
        blurred[y * width + x] = weightSum > 0 ? sum / weightSum : 0;
      }
    }
    
    return blurred;
  }
  
  // Create Gaussian kernel for blur
  createGaussianKernel(radius) {
    const size = radius * 2 + 1;
    const kernel = new Array(size);
    const sigma = radius / 3;
    let sum = 0;
    
    for (let i = 0; i < size; i++) {
      const x = i - radius;
      kernel[i] = Math.exp(-(x * x) / (2 * sigma * sigma));
      sum += kernel[i];
    }
    
    // Normalize
    for (let i = 0; i < size; i++) {
      kernel[i] /= sum;
    }
    
    return kernel;
  }

  applyBellyFlatteningEffect(ctx, img, bodyInfo, weightLossPercentage, treatments, weightLossKg = null) {
    // Create a temporary canvas for belly flattening
    const canvas = document.createElement('canvas');
    const tempCtx = canvas.getContext('2d');
    canvas.width = img.width;
    canvas.height = img.height;
    
    // Copy current content to temporary canvas
    tempCtx.putImageData(ctx.getImageData(0, 0, img.width, img.height), 0, 0);
    
    // Calculate belly flattening intensity - EXTREME VERSION
    let flatteningIntensity;
    if (weightLossKg && weightLossKg > 0) {
      // EXTREME flattening: 0.15 intensity per kg (3x stronger!)
      flatteningIntensity = Math.min(weightLossKg * 0.15 + (treatments / 40 * 0.08), 0.8); // TRIPLED!
      console.log(`EXTREME Belly flattening: ${weightLossKg}kg → intensity: ${flatteningIntensity}`);
    } else {
      flatteningIntensity = Math.min(weightLossPercentage * 0.04 + (treatments / 40 * 0.08), 0.4);
    }
    
    // Define belly area (stomach/abdominal region)
    const centerX = bodyInfo.centerX * canvas.width;
    const bodyTop = bodyInfo.shouldersY * canvas.height;
    const bodyBottom = bodyInfo.hipsY * canvas.height + (bodyInfo.hipsY - bodyInfo.shouldersY) * canvas.height * 0.3;
    const bodyWidth = (bodyInfo.rightBound - bodyInfo.leftBound) * canvas.width;
    
    // Focus on belly region (middle section of torso)
    const bellyTop = bodyTop + (bodyBottom - bodyTop) * 0.25; // Start 25% down from shoulders
    const bellyBottom = bodyTop + (bodyBottom - bodyTop) * 0.75; // End 75% down from shoulders
    const bellyLeft = centerX - bodyWidth * 0.35; // Belly width zone
    const bellyRight = centerX + bodyWidth * 0.35;
    
    // Get image data for belly flattening
    const imageData = tempCtx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
    
    // Apply concave transformation for flatter belly
    for (let y = Math.floor(bellyTop); y < Math.floor(bellyBottom); y++) {
      for (let x = Math.floor(bellyLeft); x < Math.floor(bellyRight); x++) {
        if (x >= 0 && x < canvas.width && y >= 0 && y < canvas.height) {
          // ULTRA-STRICT background protection - ONLY transform confirmed body pixels
          const bodyLeft = bodyInfo.leftBound * canvas.width;
          const bodyRight = bodyInfo.rightBound * canvas.width;
          if (!this.isPixelOnBody(x, y, centerX, bodyTop, bodyBottom, bodyLeft, bodyRight)) {
            continue; // Skip background pixels completely
          }
          
          // Calculate position within belly region
          const relativeX = (x - bellyLeft) / (bellyRight - bellyLeft); // 0 to 1
          const relativeY = (y - bellyTop) / (bellyBottom - bellyTop); // 0 to 1
          
          // Create concave curve for belly flattening (stronger in center)
          const distanceFromCenterX = Math.abs(relativeX - 0.5) * 2; // 0 at center, 1 at edges
          const distanceFromCenterY = Math.abs(relativeY - 0.5) * 2; // 0 at center, 1 at edges
          
          // Smooth falloff curves
          const horizontalFactor = 1 - Math.pow(distanceFromCenterX, 1.5); // Curved falloff
          const verticalFactor = Math.sin(relativeY * Math.PI) * 0.9 + 0.1; // Bell curve
          
          // Combined intensity for belly flattening
          const bellyFactor = horizontalFactor * verticalFactor;
          
          if (bellyFactor > 0.1) {
            // Calculate flattening displacement (inward/concave effect)
            const flatteningAmount = flatteningIntensity * bellyFactor;
            
            // Create EXTREME concave belly curve (pulls STRONGLY inward)
            const offsetFromCenter = x - centerX;
            const concaveFactor = 1 - Math.pow(Math.abs(offsetFromCenter) / (bodyWidth * 0.35), 0.4); // 0.4 for even stronger curve
            const inwardPull = flatteningAmount * concaveFactor * 5.0; // 5.0x multiplier for EXTREME effect
            
            // Apply EXTREME inward pull for ultra-flat belly
            const sourceX = Math.round(x + (offsetFromCenter > 0 ? -inwardPull : inwardPull) * canvas.width * 8); // 8x for extreme flattening
            
            if (sourceX >= 0 && sourceX < canvas.width) {
              const targetIndex = (y * canvas.width + x) * 4;
              const sourceIndex = (y * canvas.width + sourceX) * 4;
              
              // Apply stronger flattening with enhanced brightness adjustment (flatter = more defined)
              data[targetIndex] = Math.min(255, data[sourceIndex] * 1.03);     // R - Increased from 1.01 to 1.03
              data[targetIndex + 1] = Math.min(255, data[sourceIndex + 1] * 1.03); // G
              data[targetIndex + 2] = Math.min(255, data[sourceIndex + 2] * 1.03); // B
              data[targetIndex + 3] = data[sourceIndex + 3]; // A
            }
          }
        }
      }
    }
    
    // Apply the flattened belly
    tempCtx.putImageData(imageData, 0, 0);
    ctx.drawImage(canvas, 0, 0);
  }

  applyBellyLiftingEffect(ctx, img, bodyInfo, weightLossPercentage, treatments, weightLossKg = null) {
    // Create a temporary canvas for belly lifting
    const canvas = document.createElement('canvas');
    const tempCtx = canvas.getContext('2d');
    canvas.width = img.width;
    canvas.height = img.height;
    
    // Copy current content to temporary canvas
    tempCtx.putImageData(ctx.getImageData(0, 0, img.width, img.height), 0, 0);
    
    // Calculate belly lifting intensity - ULTRA-EXTREME VERSION
    let liftingIntensity;
    if (weightLossKg && weightLossKg > 0) {
      // ULTRA-EXTREME: 0.6 intensity per kg (almost DOUBLED!)
      liftingIntensity = Math.min(weightLossKg * 0.6 + (treatments / 40 * 0.4), 3.5); // EXTREME!
      console.log(`ULTRA-EXTREME Belly lifting: ${weightLossKg}kg → intensity: ${liftingIntensity}`);
    } else {
      liftingIntensity = Math.min(weightLossPercentage * 0.35 + (treatments / 40 * 0.5), 2.5);
    }
    
    // Define belly lifting area (lower belly region)
    const centerX = bodyInfo.centerX * canvas.width;
    const bodyTop = bodyInfo.shouldersY * canvas.height;
    const bodyBottom = bodyInfo.hipsY * canvas.height + (bodyInfo.hipsY - bodyInfo.shouldersY) * canvas.height * 0.3;
    const bodyWidth = (bodyInfo.rightBound - bodyInfo.leftBound) * canvas.width;
    
    // Focus on expanded lower belly region for EXTREME lifting
    const liftingTop = bodyTop + (bodyBottom - bodyTop) * 0.4; // Start 40% down from shoulders (expanded from 50%)
    const liftingBottom = bodyTop + (bodyBottom - bodyTop) * 0.95; // End 95% down from shoulders (expanded from 90%)
    const liftingLeft = centerX - bodyWidth * 0.5; // Wider zone for lifting (expanded from 0.4)
    const liftingRight = centerX + bodyWidth * 0.5;
    
    // Get image data for belly lifting
    const imageData = tempCtx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
    
    // Apply vertical lifting transformation (anti-gravity effect)
    for (let y = Math.floor(liftingTop); y < Math.floor(liftingBottom); y++) {
      for (let x = Math.floor(liftingLeft); x < Math.floor(liftingRight); x++) {
        if (x >= 0 && x < canvas.width && y >= 0 && y < canvas.height) {
          
          // Calculate position within lifting region
          const relativeX = (x - liftingLeft) / (liftingRight - liftingLeft); // 0 to 1
          const relativeY = (y - liftingTop) / (liftingBottom - liftingTop); // 0 to 1
          
          // Create ULTRA-EXTREME lifting curve
          const distanceFromCenterX = Math.abs(relativeX - 0.5) * 2;
          const verticalWeight = Math.pow(relativeY, 0.9); // EXTREME lifting curve
          
          // Ultra-wide horizontal falloff
          const horizontalFactor = 1 - Math.pow(distanceFromCenterX, 0.5); // Even wider
          
          // Combined ULTRA-EXTREME lifting factor
          const liftingFactor = horizontalFactor * verticalWeight * 5.5; // 5.5x multiplier!
          
          if (liftingFactor > 0.1) {
            // Calculate EXTREME upward displacement
            const liftAmount = liftingIntensity * liftingFactor;
            
            // Apply ULTRA-EXTREME upward lifting
            const sourceY = Math.min(canvas.height - 1, Math.max(0, Math.round(y + liftAmount * 60))); // 60px max lift!
            
            if (sourceY >= 0 && sourceY < canvas.height) {
              const targetIndex = (y * canvas.width + x) * 4;
              const sourceIndex = (sourceY * canvas.width + x) * 4;
              
              // Apply ULTRA-EXTREME lifting with skin tightening
              data[targetIndex] = Math.min(255, data[sourceIndex] * 1.35);     // EXTREME brightness
              data[targetIndex + 1] = Math.min(255, data[sourceIndex + 1] * 1.35);
              data[targetIndex + 2] = Math.min(255, data[sourceIndex + 2] * 1.35);
              data[targetIndex + 3] = data[sourceIndex + 3];
            }
          }
        }
      }
    }
    
    // Apply the lifted belly
    tempCtx.putImageData(imageData, 0, 0);
    ctx.drawImage(canvas, 0, 0);
  }

  applyChestLiftingEffect(ctx, img, bodyInfo, weightLossPercentage, treatments, weightLossKg = null) {
    // Create a temporary canvas for chest lifting
    const canvas = document.createElement('canvas');
    const tempCtx = canvas.getContext('2d');
    canvas.width = img.width;
    canvas.height = img.height;
    
    // Copy current content to temporary canvas
    tempCtx.putImageData(ctx.getImageData(0, 0, img.width, img.height), 0, 0);
    
    // Calculate chest lifting intensity - ULTRA-EXTREME VERSION
    let liftingIntensity;
    if (weightLossKg && weightLossKg > 0) {
      // ULTRA-EXTREME chest: 0.5 intensity per kg
      liftingIntensity = Math.min(weightLossKg * 0.5 + (treatments / 40 * 0.35), 2.8); // EXTREME!
      console.log(`ULTRA-EXTREME Chest lifting: ${weightLossKg}kg → intensity: ${liftingIntensity}`);
    } else {
      liftingIntensity = Math.min(weightLossPercentage * 0.25 + (treatments / 40 * 0.4), 2.0);
    }
    
    // Define chest lifting area (upper torso/bust region)
    const centerX = bodyInfo.centerX * canvas.width;
    const bodyTop = bodyInfo.shouldersY * canvas.height;
    const bodyBottom = bodyInfo.hipsY * canvas.height;
    const bodyWidth = (bodyInfo.rightBound - bodyInfo.leftBound) * canvas.width;
    
    // Focus on chest/bust region for EXTREME lifting
    const chestTop = bodyTop + (bodyBottom - bodyTop) * 0.05; // Start very close to shoulders
    const chestBottom = bodyTop + (bodyBottom - bodyTop) * 0.45; // End at mid-torso
    const chestLeft = centerX - bodyWidth * 0.45; // Wide chest zone
    const chestRight = centerX + bodyWidth * 0.45;
    
    // Get image data for chest lifting
    const imageData = tempCtx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
    
    // Apply vertical lifting transformation for chest (anti-gravity effect)
    for (let y = Math.floor(chestTop); y < Math.floor(chestBottom); y++) {
      for (let x = Math.floor(chestLeft); x < Math.floor(chestRight); x++) {
        if (x >= 0 && x < canvas.width && y >= 0 && y < canvas.height) {
          
          // Calculate position within chest lifting region
          const relativeX = (x - chestLeft) / (chestRight - chestLeft); // 0 to 1
          const relativeY = (y - chestTop) / (chestBottom - chestTop); // 0 to 1
          
          // Create ULTRA-EXTREME chest lifting curve
          const distanceFromCenterX = Math.abs(relativeX - 0.5) * 2;
          const verticalWeight = Math.pow(1 - relativeY, 0.7); // EXTREME at chest
          
          // Enhanced horizontal falloff
          const horizontalFactor = 1 - Math.pow(distanceFromCenterX, 0.6);
          
          // Combined ULTRA-EXTREME chest lifting factor
          const liftingFactor = horizontalFactor * verticalWeight * 5.0; // 5.0x multiplier!
          
          if (liftingFactor > 0.1) {
            // Calculate EXTREME upward displacement for chest
            const liftAmount = liftingIntensity * liftingFactor;
            
            // Apply ULTRA-EXTREME upward chest lifting
            const sourceY = Math.min(canvas.height - 1, Math.max(0, Math.round(y + liftAmount * 65))); // 65px max lift!
            
            if (sourceY !== y && sourceY < canvas.height) {
              // Copy pixel from lower position to create lifting effect
              const targetIndex = (y * canvas.width + x) * 4;
              const sourceIndex = (sourceY * canvas.width + x) * 4;
              
              // Enhanced blending for natural chest lifting
              const blendFactor = Math.min(liftingFactor, 0.9);
              
              data[targetIndex] = Math.round(data[targetIndex] * (1 - blendFactor) + data[sourceIndex] * blendFactor); // R
              data[targetIndex + 1] = Math.round(data[targetIndex + 1] * (1 - blendFactor) + data[sourceIndex + 1] * blendFactor); // G
              data[targetIndex + 2] = Math.round(data[targetIndex + 2] * (1 - blendFactor) + data[sourceIndex + 2] * blendFactor); // B
              data[targetIndex + 3] = Math.round(data[targetIndex + 3] * (1 - blendFactor) + data[sourceIndex + 3] * blendFactor); // A
            }
          }
        }
      }
    }
    
    // Apply the lifted chest
    tempCtx.putImageData(imageData, 0, 0);
    ctx.drawImage(canvas, 0, 0);
  }

  applyFatRollReduction(ctx, img, bodyInfo, weightLossPercentage, treatments, weightLossKg = null) {
    // Create a temporary canvas for fat roll reduction
    const canvas = document.createElement('canvas');
    const tempCtx = canvas.getContext('2d');
    canvas.width = img.width;
    canvas.height = img.height;
    
    // Copy current content to temporary canvas
    tempCtx.putImageData(ctx.getImageData(0, 0, img.width, img.height), 0, 0);
    
    // Calculate fat roll reduction intensity based on actual kg loss
    let reductionIntensity;
    if (weightLossKg && weightLossKg > 0) {
      // DRAMATICALLY increased fat roll reduction: 0.035 intensity per kg lost for strong fat reduction
      reductionIntensity = Math.min(weightLossKg * 0.035 + (treatments / 40 * 0.025), 0.200); // Increased from 0.012/0.100 to 0.035/0.200
      console.log(`ENHANCED Fat roll reduction based on kg: ${weightLossKg}kg → intensity: ${reductionIntensity}`);
    } else {
      // Fallback to percentage-based
      reductionIntensity = Math.min(weightLossPercentage * 0.010 + (treatments / 40 * 0.020), 0.080);
    }
    
    // Define body areas for fat roll reduction
    const centerX = bodyInfo.centerX * canvas.width;
    const bodyTop = bodyInfo.shouldersY * canvas.height;
    const bodyBottom = bodyInfo.hipsY * canvas.height + (bodyInfo.hipsY - bodyInfo.shouldersY) * canvas.height * 0.3;
    const bodyWidth = (bodyInfo.rightBound - bodyInfo.leftBound) * canvas.width;
    
    // Focus on side fat roll areas (love handles)
    const sideRollTop = bodyTop + (bodyBottom - bodyTop) * 0.3;
    const sideRollBottom = bodyTop + (bodyBottom - bodyTop) * 0.8;
    
    // Left side fat rolls
    const leftRollLeft = centerX - bodyWidth * 0.6;
    const leftRollRight = centerX - bodyWidth * 0.2;
    
    // Right side fat rolls  
    const rightRollLeft = centerX + bodyWidth * 0.2;
    const rightRollRight = centerX + bodyWidth * 0.6;
    
    // Front fat rolls (muffin top area)
    const frontRollTop = bodyTop + (bodyBottom - bodyTop) * 0.25;
    const frontRollBottom = bodyTop + (bodyBottom - bodyTop) * 0.65;
    const frontRollLeft = centerX - bodyWidth * 0.25;
    const frontRollRight = centerX + bodyWidth * 0.25;
    
    // Get image data for fat roll reduction
    const imageData = tempCtx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
    
    // Apply fat roll smoothing to left side
    this.applyFatRollSmoothing(data, canvas.width, canvas.height, 
      leftRollLeft, leftRollRight, sideRollTop, sideRollBottom, 
      centerX, reductionIntensity, 'left');
    
    // Apply fat roll smoothing to right side
    this.applyFatRollSmoothing(data, canvas.width, canvas.height,
      rightRollLeft, rightRollRight, sideRollTop, sideRollBottom,
      centerX, reductionIntensity, 'right');
    
    // Apply front fat roll smoothing
    this.applyFatRollSmoothing(data, canvas.width, canvas.height,
      frontRollLeft, frontRollRight, frontRollTop, frontRollBottom,
      centerX, reductionIntensity, 'front');
    
    // Apply the fat roll reduced image
    tempCtx.putImageData(imageData, 0, 0);
    ctx.drawImage(canvas, 0, 0);
  }

  applyFatRollSmoothing(data, width, height, left, right, top, bottom, centerX, intensity, area) {
    for (let y = Math.floor(top); y < Math.floor(bottom); y++) {
      for (let x = Math.floor(left); x < Math.floor(right); x++) {
        if (x >= 0 && x < width && y >= 0 && y < height) {
          
          // Calculate position within fat roll region
          const relativeX = (x - left) / (right - left);
          const relativeY = (y - top) / (bottom - top);
          
          // Create fat roll reduction curve
          let reductionFactor = 0;
          
          if (area === 'front') {
            // Front fat rolls - reduce bulging outward
            const distanceFromCenter = Math.abs(x - centerX) / ((right - left) / 2);
            reductionFactor = (1 - Math.pow(distanceFromCenter, 0.8)) * Math.sin(relativeY * Math.PI) * intensity;
          } else {
            // Side fat rolls - smooth inward toward body center
            const isLeft = area === 'left';
            const edgeDistance = isLeft ? (relativeX) : (1 - relativeX);
            reductionFactor = Math.pow(edgeDistance, 0.6) * Math.sin(relativeY * Math.PI) * intensity;
          }
          
          if (reductionFactor > 0.05) {
            // Apply smoothing transformation
            let sourceX = x;
            
            if (area === 'front') {
              // Pull front fat rolls inward
              const pullDirection = x > centerX ? -1 : 1;
              sourceX = Math.round(x + pullDirection * reductionFactor * width * 2);
            } else {
              // Smooth side fat rolls toward center
              const pullTowardCenter = (centerX - x) * reductionFactor * 0.8;
              sourceX = Math.round(x + pullTowardCenter);
            }
            
            if (sourceX >= 0 && sourceX < width) {
              const targetIndex = (y * width + x) * 4;
              const sourceIndex = (y * width + sourceX) * 4;
              
              // Apply fat roll reduction with skin tightening
              data[targetIndex] = Math.min(255, data[sourceIndex] * 1.06);
              data[targetIndex + 1] = Math.min(255, data[sourceIndex + 1] * 1.06);
              data[targetIndex + 2] = Math.min(255, data[sourceIndex + 2] * 1.06);
              data[targetIndex + 3] = data[sourceIndex + 3];
            }
          }
        }
      }
    }
  }

  calculateSmoothIntensity(x, y, bodyCenterX, bodyTop, bodyBottom, bodyLeft, bodyRight) {
    // Calculate normalized position within body
    const relativeX = (x - bodyLeft) / (bodyRight - bodyLeft);
    const relativeY = (y - bodyTop) / (bodyBottom - bodyTop);
    
    // Distance from horizontal center (0 at center, 1 at edges)
    const horizontalDistance = Math.abs(relativeX - 0.5) * 2;
    
    // CONTINUOUS vertical intensity - smooth bell curve across entire body
    // No zones, no if-statements - just one smooth mathematical function
    const yFromCenter = Math.abs(relativeY - 0.5); // Distance from vertical center
    const verticalIntensity = Math.exp(-yFromCenter * yFromCenter * 4.0) * 0.8 + 0.4;
    
    // Smooth horizontal falloff to create natural body shape
    const horizontalIntensity = 1 - Math.pow(horizontalDistance, 1.5);
    
    return Math.max(0.1, verticalIntensity * horizontalIntensity);
  }

  isPixelOnBody(x, y, centerX, bodyTop, bodyBottom, bodyLeft, bodyRight) {
    // ULTRA-PRECISE body detection to COMPLETELY avoid transforming background
    const isInVerticalRange = y >= bodyTop && y <= bodyBottom;
    const isInHorizontalRange = x >= bodyLeft && x <= bodyRight;
    
    if (!isInVerticalRange || !isInHorizontalRange) {
      return false;
    }
    
    // Use MUCH MORE STRICT elliptical body shape for MAXIMUM background protection
    const relativeX = (x - centerX) / ((bodyRight - bodyLeft) / 2);
    const relativeY = (y - bodyTop) / (bodyBottom - bodyTop);
    
    // ULTRA-STRICT elliptical bounds with ZERO background tolerance
    const ellipticalDistance = Math.pow(relativeX, 2) + Math.pow((relativeY - 0.5) * 1.8, 2); // More vertical compression
    
    // Additional gradient-based body detection for MAXIMUM precision
    const bodyCore = ellipticalDistance <= 0.6;  // Core body area - always safe
    const bodyEdge = ellipticalDistance <= 0.85; // Edge area - needs validation
    
    if (bodyCore) return true;
    if (!bodyEdge) return false;
    
    // For edge pixels, add additional validation to avoid background
    const distanceFromCenter = Math.sqrt(Math.pow(x - centerX, 2) + Math.pow(y - (bodyTop + bodyBottom) / 2, 2));
    const bodyRadius = Math.min((bodyRight - bodyLeft) / 2, (bodyBottom - bodyTop) / 2) * 0.75; // Tighter radius
    
    return distanceFromCenter <= bodyRadius;
  }

  getEdgeProtectionFactor(x, y, centerX, bodyTop, bodyBottom, bodyLeft, bodyRight) {
    // Ultra-smooth edge protection for seamless transitions
    const distanceFromCenterX = Math.abs(x - centerX) / ((bodyRight - bodyLeft) / 2);
    const distanceFromCenterY = Math.abs((y - (bodyTop + bodyBottom) / 2)) / ((bodyBottom - bodyTop) / 2);
    
    // Single smooth gaussian function for all directions
    const horizontalFalloff = Math.exp(-distanceFromCenterX * distanceFromCenterX * 1.5);
    const verticalFalloff = Math.exp(-distanceFromCenterY * distanceFromCenterY * 1.0);
    
    // Combined smooth protection
    const smoothProtection = horizontalFalloff * verticalFalloff;
    
    // Ensure minimum protection and smooth transition to zero
    return Math.max(0.1, smoothProtection);
  }

  applySmoothTransition(rawTransformation, x, y, centerX, bodyTop, bodyBottom) {
    // Apply smooth gaussian-like transition to prevent block artifacts
    const relativeY = (y - bodyTop) / (bodyBottom - bodyTop);
    
    // Create smooth transition zones to prevent discontinuities
    let smoothingFactor = 1.0;
    
    // Smooth transition at zone boundaries
    if (relativeY < 0.25) {
      // Shoulder to upper torso transition
      const t = relativeY / 0.25;
      smoothingFactor = 0.3 + 0.7 * (3 * t * t - 2 * t * t * t); // Smooth cubic interpolation
    } else if (relativeY > 0.75) {
      // Lower torso to hip transition  
      const t = (1 - relativeY) / 0.25;
      smoothingFactor = 0.3 + 0.7 * (3 * t * t - 2 * t * t * t);
    }
    
    // Distance-based smoothing for horizontal continuity
    const distanceFromCenter = Math.abs(x - centerX) / ((bodyBottom - bodyTop) * 0.5);
    const horizontalSmoothing = Math.exp(-distanceFromCenter * distanceFromCenter * 2);
    
    return rawTransformation * smoothingFactor * horizontalSmoothing;
  }

  getContinuityFactor(x, y, transformation, centerX, bodyTop, bodyBottom) {
    // Ensure smooth transitions between adjacent pixels
    const relativeY = (y - bodyTop) / (bodyBottom - bodyTop);
    const relativeX = Math.abs(x - centerX) / ((bodyBottom - bodyTop) * 0.5);
    
    // Reduce transformation at zone boundaries to maintain continuity
    let continuityFactor = 1.0;
    
    // Smooth boundaries between transformation zones
    if (relativeY < 0.3 || relativeY > 0.7) {
      continuityFactor *= 0.7; // Reduce transformation at top/bottom
    }
    
    if (relativeX > 0.6) {
      continuityFactor *= 0.5; // Reduce transformation at sides
    }
    
    // Apply gradient-based smoothing
    const gradientSmoothing = Math.exp(-Math.pow(relativeX, 2) - Math.pow(Math.abs(relativeY - 0.5), 2));
    
    return Math.max(0.1, continuityFactor * gradientSmoothing);
  }

  createContinuousTransformationMap(width, height, centerX, bodyTop, bodyBottom, bodyLeft, bodyRight, weightLossPercentage, treatments, weightLossKg) {
    // Create 2D array for transformation mapping
    const transformationMap = Array(height).fill().map(() => Array(width).fill(0));
    
    // Calculate base transformation intensity
    let baseTransformation;
    if (weightLossKg && weightLossKg > 0) {
      baseTransformation = Math.min(weightLossKg * 0.035 + (treatments / 40 * 0.025), 0.400);
    } else {
      baseTransformation = Math.min(weightLossPercentage * 0.020 + (treatments / 40 * 0.020), 0.300);
    }
    
    // Pre-calculate transformation for each pixel to ensure continuity
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        // Initialize with identity mapping (no transformation)
        transformationMap[y][x] = x;
        
        // Extended region to include arm areas
        const extendedBodyLeft = bodyLeft - width * 0.15;
        const extendedBodyRight = bodyRight + width * 0.15;
        
        // Only process pixels in extended body region
        if (x >= extendedBodyLeft && x <= extendedBodyRight && y >= bodyTop && y <= bodyBottom) {
          // Check if pixel is actually on the body
          if (this.isPixelOnBody(x, y, centerX, bodyTop, bodyBottom, bodyLeft, bodyRight)) {
            // Calculate smooth transformation intensity
            const transformationIntensity = this.calculateSmoothIntensity(x, y, centerX, bodyTop, bodyBottom, bodyLeft, bodyRight);
            
            if (transformationIntensity > 0.01) {
              // Calculate offset from center
              const offsetFromCenter = x - centerX;
              const expansion = baseTransformation * transformationIntensity;
              
              // Apply moderate torso boost with smooth transitions
              const relativeY = (y - bodyTop) / (bodyBottom - bodyTop);
              let torsoBoost = 1.0;
              
              // Smooth torso boost with gaussian curve
              if (relativeY >= 0.25 && relativeY <= 0.75) {
                const torsoCenter = 0.5;
                const distance = Math.abs(relativeY - torsoCenter) / 0.25;
                torsoBoost = 1.0 + 1.5 * Math.exp(-distance * distance * 2); // Smooth gaussian boost
              }
              
              // Get edge protection
              const edgeProtection = this.getEdgeProtectionFactor(x, y, centerX, bodyTop, bodyBottom, bodyLeft, bodyRight);
              
              // Calculate final transformation
              const finalTransformation = expansion * torsoBoost * edgeProtection;
              const sourceOffset = offsetFromCenter * (1 + finalTransformation);
              const newSourceX = centerX + sourceOffset;
              
              // Store in transformation map with bounds checking
              transformationMap[y][x] = Math.max(0, Math.min(width - 1, Math.round(newSourceX)));
            }
          }
        }
      }
    }
    
    // Apply smoothing filter to ensure continuity between neighboring pixels
    return this.smoothTransformationMap(transformationMap, width, height, centerX, bodyTop, bodyBottom);
  }

  smoothTransformationMap(transformationMap, width, height, centerX, bodyTop, bodyBottom) {
    const smoothedMap = Array(height).fill().map(() => Array(width).fill(0));
    
    // Apply gaussian smoothing to eliminate discontinuities
    const kernelSize = 3;
    const sigma = 1.0;
    
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        let weightedSum = 0;
        let totalWeight = 0;
        
        // Apply smoothing kernel
        for (let ky = -kernelSize; ky <= kernelSize; ky++) {
          for (let kx = -kernelSize; kx <= kernelSize; kx++) {
            const ny = y + ky;
            const nx = x + kx;
            
            if (ny >= 0 && ny < height && nx >= 0 && nx < width) {
              const distance = Math.sqrt(kx * kx + ky * ky);
              const weight = Math.exp(-(distance * distance) / (2 * sigma * sigma));
              
              weightedSum += transformationMap[ny][nx] * weight;
              totalWeight += weight;
            }
          }
        }
        
        // Store smoothed value
        if (totalWeight > 0) {
          smoothedMap[y][x] = Math.round(weightedSum / totalWeight);
        } else {
          smoothedMap[y][x] = transformationMap[y][x];
        }
      }
    }
    
    return smoothedMap;
  }

  applyEdgeSmoothing(ctx, bodyCenterX, bodyTop, bodyBottom, bodyLeft, bodyRight) {
    
    const imageData = ctx.getImageData(0, 0, ctx.canvas.width, ctx.canvas.height);
    const data = imageData.data;
    const width = ctx.canvas.width;
    const height = ctx.canvas.height;
    
    // Create smoothing kernels
    const standardKernel = [
      [0.05, 0.1, 0.05],
      [0.1,  0.4, 0.1],
      [0.05, 0.1, 0.05]
    ];
    
    // Extra soft kernel for arms
    const armKernel = [
      [0.08, 0.12, 0.08],
      [0.12, 0.2, 0.12],
      [0.08, 0.12, 0.08]
    ];
    
    // Apply smoothing with extended area for arms
    const armTopY = bodyTop;
    const armBottomY = bodyTop + (bodyBottom - bodyTop) * 0.6;
    
    for (let y = Math.max(1, bodyTop - 15); y < Math.min(height - 1, bodyBottom + 15); y++) {
      for (let x = Math.max(1, bodyLeft - 40); x < Math.min(width - 1, bodyRight + 40); x++) {
        
        // Check if we're in a transition zone
        const isInTransition = this.isInTransitionZone(x, y, bodyCenterX, bodyTop, bodyBottom, bodyLeft, bodyRight);
        
        if (isInTransition) {
          const pixelIndex = (y * width + x) * 4;
          
          // Determine if we're in arm area for special treatment
          const isInArmArea = y >= armTopY && y <= armBottomY;
          const isInLeftArmArea = isInArmArea && x < bodyLeft + 35;
          const isInRightArmArea = isInArmArea && x > bodyRight - 35;
          const isInArmZone = isInLeftArmArea || isInRightArmArea;
          
          // Choose appropriate kernel and blend factor
          const kernel = isInArmZone ? armKernel : standardKernel;
          const blendFactor = isInArmZone ? 0.5 : 0.3; // Stronger smoothing for arms
          
          // Apply weighted average smoothing
          let sumR = 0, sumG = 0, sumB = 0, sumA = 0;
          let totalWeight = 0;
          
          // Use larger smoothing area for arms
          const kernelSize = isInArmZone ? 2 : 1;
          
          for (let ky = -kernelSize; ky <= kernelSize; ky++) {
            for (let kx = -kernelSize; kx <= kernelSize; kx++) {
              const ny = y + ky;
              const nx = x + kx;
              
              if (ny >= 0 && ny < height && nx >= 0 && nx < width) {
                const neighborIndex = (ny * width + nx) * 4;
                
                // Calculate weight based on distance for larger kernel
                let weight;
                if (kernelSize === 2) {
                  const distance = Math.sqrt(kx * kx + ky * ky);
                  weight = Math.max(0.02, Math.exp(-distance * distance * 0.5));
                } else {
                  weight = kernel[ky + 1][kx + 1];
                }
                
                sumR += data[neighborIndex] * weight;
                sumG += data[neighborIndex + 1] * weight;
                sumB += data[neighborIndex + 2] * weight;
                sumA += data[neighborIndex + 3] * weight;
                totalWeight += weight;
              }
            }
          }
          
          // Blend smoothed values with original
          data[pixelIndex] = data[pixelIndex] * (1 - blendFactor) + (sumR / totalWeight) * blendFactor;
          data[pixelIndex + 1] = data[pixelIndex + 1] * (1 - blendFactor) + (sumG / totalWeight) * blendFactor;
          data[pixelIndex + 2] = data[pixelIndex + 2] * (1 - blendFactor) + (sumB / totalWeight) * blendFactor;
          data[pixelIndex + 3] = data[pixelIndex + 3] * (1 - blendFactor) + (sumA / totalWeight) * blendFactor;
        }
      }
    }
    
    ctx.putImageData(imageData, 0, 0);
  }

  isInTransitionZone(x, y, bodyCenterX, bodyTop, bodyBottom, bodyLeft, bodyRight) {
    // Calculate distance to transformation boundary
    const relativeX = (x - bodyLeft) / (bodyRight - bodyLeft);
    const relativeY = (y - bodyTop) / (bodyBottom - bodyTop);
    
    // Standard edge distances
    const edgeDistance = 20; // pixels
    const armEdgeDistance = 35; // Larger distance for arms
    
    // Check if we're near the edges of the transformation area
    const isNearLeftEdge = x < bodyLeft + edgeDistance;
    const isNearRightEdge = x > bodyRight - edgeDistance;
    const isNearTopEdge = y < bodyTop + edgeDistance;
    const isNearBottomEdge = y > bodyBottom - edgeDistance;
    
    // Special handling for arm areas (left and right sides)
    const armTopY = bodyTop;
    const armBottomY = bodyTop + (bodyBottom - bodyTop) * 0.6; // Upper 60% of body
    const isInArmArea = y >= armTopY && y <= armBottomY;
    
    const isInLeftArmZone = isInArmArea && x < bodyLeft + armEdgeDistance;
    const isInRightArmZone = isInArmArea && x > bodyRight - armEdgeDistance;
    
    // Horizontal falloff zones
    const horizontalDistance = Math.abs(relativeX - 0.5) * 2;
    const isInFalloffZone = horizontalDistance > 0.6;
    
    // Additional smoothing around arm attachment points (shoulders)
    const shoulderY = bodyTop + (bodyBottom - bodyTop) * 0.2;
    const isNearShoulders = Math.abs(y - shoulderY) < 25 && (isInLeftArmZone || isInRightArmZone);
    
    return isNearLeftEdge || isNearRightEdge || isNearTopEdge || isNearBottomEdge || 
           isInFalloffZone || isInLeftArmZone || isInRightArmZone || isNearShoulders;
  }

  defineBodyRegions(bodyInfo, width, height) {
    const regions = [];
    
    // Bauchregion (Stomach/Waist) - Höchste Priorität
    regions.push({
      name: 'Bauch',
      centerX: bodyInfo.centerX * width,
      top: Math.max(bodyInfo.shouldersY * height + (bodyInfo.hipsY - bodyInfo.shouldersY) * height * 0.4, 0),
      bottom: Math.min(bodyInfo.hipsY * height + (bodyInfo.hipsY - bodyInfo.shouldersY) * height * 0.2, height),
      leftBound: (bodyInfo.leftBound * width) - width * 0.05,
      rightBound: (bodyInfo.rightBound * width) + width * 0.05,
      slimFactor: 1.0, // Maximum slimming
      falloffRadius: width * 0.15
    });
    
    // Hüftregion (Hips)
    regions.push({
      name: 'Hüfte',
      centerX: bodyInfo.centerX * width,
      top: Math.max(bodyInfo.hipsY * height - height * 0.05, 0),
      bottom: Math.min(bodyInfo.hipsY * height + height * 0.15, height),
      leftBound: (bodyInfo.leftBound * width) - width * 0.08,
      rightBound: (bodyInfo.rightBound * width) + width * 0.08,
      slimFactor: 0.8,
      falloffRadius: width * 0.12
    });
    
    // Oberschenkel/Po-Region (Upper Legs/Thighs)
    regions.push({
      name: 'Oberschenkel',
      centerX: bodyInfo.centerX * width,
      top: Math.max(bodyInfo.hipsY * height + height * 0.05, 0),
      bottom: Math.min(bodyInfo.hipsY * height + height * 0.25, height),
      leftBound: (bodyInfo.leftBound * width) - width * 0.06,
      rightBound: (bodyInfo.rightBound * width) + width * 0.06,
      slimFactor: 0.6,
      falloffRadius: width * 0.10
    });
    
    // Oberkörper (Upper Body/Torso)
    regions.push({
      name: 'Oberkörper',
      centerX: bodyInfo.centerX * width,
      top: Math.max(bodyInfo.shouldersY * height + height * 0.08, 0),
      bottom: Math.min(bodyInfo.shouldersY * height + (bodyInfo.hipsY - bodyInfo.shouldersY) * height * 0.6, height),
      leftBound: (bodyInfo.leftBound * width) - width * 0.04,
      rightBound: (bodyInfo.rightBound * width) + width * 0.04,
      slimFactor: 0.5,
      falloffRadius: width * 0.08
    });
    
    // Arme (Arms) - falls erkannt
    if (bodyInfo.shouldersWidth > 0) {
      // Linker Arm
      regions.push({
        name: 'Linker Arm',
        centerX: bodyInfo.leftBound * width - width * 0.05,
        top: Math.max(bodyInfo.shouldersY * height, 0),
        bottom: Math.min(bodyInfo.hipsY * height + height * 0.1, height),
        leftBound: (bodyInfo.leftBound * width) - width * 0.15,
        rightBound: (bodyInfo.leftBound * width) + width * 0.03,
        slimFactor: 0.4,
        falloffRadius: width * 0.06
      });
      
      // Rechter Arm
      regions.push({
        name: 'Rechter Arm',
        centerX: bodyInfo.rightBound * width + width * 0.05,
        top: Math.max(bodyInfo.shouldersY * height, 0),
        bottom: Math.min(bodyInfo.hipsY * height + height * 0.1, height),
        leftBound: (bodyInfo.rightBound * width) - width * 0.03,
        rightBound: (bodyInfo.rightBound * width) + width * 0.15,
        slimFactor: 0.4,
        falloffRadius: width * 0.06
      });
    }
    
    return regions;
  }

  calculateRegionIntensity(x, y, region) {
    // Check if pixel is within region bounds
    if (x < region.leftBound || x > region.rightBound || 
        y < region.top || y > region.bottom) {
      return 0;
    }
    
    // Calculate elliptical distance from region center
    const centerY = (region.top + region.bottom) / 2;
    const regionWidth = region.rightBound - region.leftBound;
    const regionHeight = region.bottom - region.top;
    
    const normalizedX = (x - region.centerX) / (regionWidth / 2);
    const normalizedY = (y - centerY) / (regionHeight / 2);
    const ellipticalDistance = Math.sqrt(normalizedX * normalizedX + normalizedY * normalizedY);
    
    // Smooth falloff with configurable radius
    const falloffFactor = region.falloffRadius / (regionWidth / 2);
    const intensity = Math.max(0, 1 - (ellipticalDistance / falloffFactor));
    
    return Math.pow(intensity, 1.5); // Smoother falloff curve
  }

  createSmartTransformation(img, weightLossPercentage, treatmentCount = 10, weightLossKg) {
    
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = img.width;
    canvas.height = img.height;

    // Draw original image as base
    ctx.drawImage(img, 0, 0);
    
    // Apply localized transformation using estimated body proportions
    this.applyEstimatedBodySlimming(ctx, img, weightLossPercentage, weightLossKg);
    
    // Apply basic skin and muscle enhancements based on treatment count
    this.applyEstimatedEnhancements(ctx, img, treatmentCount);

    // Add indicator for smart transformation with kg display
    const displayText = weightLossKg ? `${weightLossKg} kg + ${treatmentCount} Behandlungen` : `Smart + ${treatmentCount} Behandlungen`;
    this.addIndicator(ctx, displayText, weightLossPercentage, '#ff8800');
    return canvas.toDataURL('image/jpeg', 0.85);
  }

  applyEstimatedBodySlimming(ctx, img, weightLossPercentage, weightLossKg = null) {
    
    const imageData = ctx.getImageData(0, 0, img.width, img.height);
    const newImageData = ctx.createImageData(img.width, img.height);
    const srcData = imageData.data;
    const dstData = newImageData.data;
    
    // Calculate transformation based on kg loss if available
    let baseTransformation;
    if (weightLossKg && weightLossKg > 0) {
      // DRAMATICALLY increased estimated slimming: 0.150 per kg for strong fallback transformation
      baseTransformation = Math.min(weightLossKg * 0.150, 0.70); // Increased from 0.050/0.35 to 0.150/0.70
      console.log(`ENHANCED Estimated slimming based on kg: ${weightLossKg}kg → transformation: ${baseTransformation}`);
    } else {
      baseTransformation = Math.min(weightLossPercentage * 0.012, 0.30);
    }
    const centerX = img.width / 2;
    
    // Estimated body dimensions
    const bodyTop = img.height * 0.15;
    const bodyBottom = img.height * 0.85;
    const bodyLeft = img.width * 0.20;
    const bodyRight = img.width * 0.80;
    
    // Process each pixel with smooth, unified transformation
    for (let y = 0; y < img.height; y++) {
      for (let x = 0; x < img.width; x++) {
        const pixelIndex = (y * img.width + x) * 4;
        let sourceX = x;
        
        // Check if in estimated body region
        if (x >= bodyLeft && x <= bodyRight && y >= bodyTop && y <= bodyBottom) {
          // Calculate smooth transformation intensity
          const transformationIntensity = this.calculateSmoothIntensity(x, y, centerX, bodyTop, bodyBottom, bodyLeft, bodyRight);
          
          if (transformationIntensity > 0.01) {
            // Apply gentle horizontal compression
            const offsetFromCenter = x - centerX;
            const expansion = baseTransformation * transformationIntensity;
            const sourceOffset = offsetFromCenter * (1 + expansion);
            sourceX = Math.round(centerX + sourceOffset);
            sourceX = Math.max(0, Math.min(img.width - 1, sourceX));
          }
        }
        
        // Copy pixel from source location
        const sourceIndex = (y * img.width + sourceX) * 4;
        dstData[pixelIndex] = srcData[sourceIndex];
        dstData[pixelIndex + 1] = srcData[sourceIndex + 1];
        dstData[pixelIndex + 2] = srcData[sourceIndex + 2];
        dstData[pixelIndex + 3] = srcData[sourceIndex + 3];
      }
    }
    
    // Apply the transformed image
    ctx.putImageData(newImageData, 0, 0);
  }

  createFallbackTransformation(imageSrc, weightLossPercentage, treatmentCount = 10) {
    return new Promise(async (resolve) => {
      
      const img = await this.loadImage(imageSrc);
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = img.width;
      canvas.height = img.height;

      // Draw original image first
      ctx.drawImage(img, 0, 0);
      
      // Apply simple but localized transformation to body areas
      this.applySimpleLocalizedSlimming(ctx, img, weightLossPercentage);
      
      // Apply basic enhancements
      this.applyEstimatedEnhancements(ctx, img, treatmentCount);

      this.addIndicator(ctx, `Fallback + ${treatmentCount} Behandlungen`, weightLossPercentage, '#ff4444');
      resolve(canvas.toDataURL('image/jpeg', 0.85));
    });
  }

  applySimpleLocalizedSlimming(ctx, img, weightLossPercentage) {
    const imageData = ctx.getImageData(0, 0, img.width, img.height);
    const newImageData = ctx.createImageData(img.width, img.height);
    const srcData = imageData.data;
    const dstData = newImageData.data;
    
    // Consistent visible transformation
    const baseTransformation = Math.min(weightLossPercentage * 0.012, 0.30);
    const centerX = img.width / 2;
    
    // Simple body region estimation
    const bodyTop = img.height * 0.15;
    const bodyBottom = img.height * 0.85;
    const bodyLeft = img.width * 0.20;
    const bodyRight = img.width * 0.80;
    
    for (let y = 0; y < img.height; y++) {
      for (let x = 0; x < img.width; x++) {
        const pixelIndex = (y * img.width + x) * 4;
        let sourceX = x;
        
        // Check if in estimated body region
        if (x >= bodyLeft && x <= bodyRight && y >= bodyTop && y <= bodyBottom) {
          // Use the same smooth intensity calculation
          const transformationIntensity = this.calculateSmoothIntensity(x, y, centerX, bodyTop, bodyBottom, bodyLeft, bodyRight);
          
          if (transformationIntensity > 0.01) {
            // Apply gentle horizontal compression
            const offsetFromCenter = x - centerX;
            const expansion = baseTransformation * transformationIntensity;
            const sourceOffset = offsetFromCenter * (1 + expansion);
            sourceX = Math.round(centerX + sourceOffset);
            sourceX = Math.max(0, Math.min(img.width - 1, sourceX));
          }
        }
        
        // Copy pixel from source location
        const sourceIndex = (y * img.width + sourceX) * 4;
        dstData[pixelIndex] = srcData[sourceIndex];
        dstData[pixelIndex + 1] = srcData[sourceIndex + 1];
        dstData[pixelIndex + 2] = srcData[sourceIndex + 2];
        dstData[pixelIndex + 3] = srcData[sourceIndex + 3];
      }
    }
    
    ctx.putImageData(newImageData, 0, 0);
  }

  applySkinTightening(ctx, img, bodyInfo, treatmentCount) {
    // Stronger skin tightening based on treatment count
    const canvas = document.createElement('canvas');
    const tempCtx = canvas.getContext('2d');
    canvas.width = img.width;
    canvas.height = img.height;
    
    // Copy current content to temporary canvas
    tempCtx.putImageData(ctx.getImageData(0, 0, img.width, img.height), 0, 0);
    
    // ULTRA-MASSIVE skin tightening intensity
    const baseTightening = 0.12 + (treatmentCount / 40 * 0.25); // MASSIVELY increased again!
    
    // Get image data
    const imageData = tempCtx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
    
    const centerX = bodyInfo.centerX * canvas.width;
    const bodyTop = bodyInfo.shouldersY * canvas.height;
    const bodyBottom = bodyInfo.hipsY * canvas.height + (bodyInfo.hipsY - bodyInfo.shouldersY) * canvas.height * 0.3;
    const bodyWidth = (bodyInfo.rightBound - bodyInfo.leftBound) * canvas.width;
    
    // Apply enhanced pixel-level tightening
    for (let y = 0; y < canvas.height; y++) {
      for (let x = 0; x < canvas.width; x++) {
        if (y >= bodyTop && y <= bodyBottom) {
          const distanceFromCenter = Math.abs(x - centerX);
          const verticalPosition = (y - bodyTop) / (bodyBottom - bodyTop);
          
          // ULTRA-MASSIVE tightening zones
          if (distanceFromCenter < bodyWidth * 0.95) { // Almost full body width
            // Calculate ULTRA-EXTREME tightening factor
            const horizontalFactor = 1 - (distanceFromCenter / (bodyWidth * 0.95));
            const verticalFactor = Math.sin(verticalPosition * Math.PI);
            let tighteningFactor = baseTightening * horizontalFactor * verticalFactor * 8; // 8x multiplier!
            
            // ULTRA-EXTREME tightening for stomach area
            if (verticalPosition > 0.1 && verticalPosition < 0.98) {
              tighteningFactor *= 5.5; // MASSIVE boost!
            }
            
            // Apply the ULTRA-EXTREME tightening effect
            const sourceX = Math.round(x + (x > centerX ? tighteningFactor : -tighteningFactor) * canvas.width);
            
            if (sourceX >= 0 && sourceX < canvas.width) {
              const targetIndex = (y * canvas.width + x) * 4;
              const sourceIndex = (y * canvas.width + sourceX) * 4;
              
              // ULTRA-MASSIVE brightness boost for extremely tight skin
              data[targetIndex] = Math.min(255, data[sourceIndex] * 1.40); // ULTRA brightness!
              data[targetIndex + 1] = Math.min(255, data[sourceIndex + 1] * 1.40);
              data[targetIndex + 2] = Math.min(255, data[sourceIndex + 2] * 1.40);
              data[targetIndex + 3] = data[sourceIndex + 3];
            }
          }
        }
      }
    }
    
    tempCtx.putImageData(imageData, 0, 0);
    ctx.drawImage(canvas, 0, 0);
  }

  applyAbdominalMuscleEnhancement(ctx, bodyInfo, treatmentCount) {
    
    // Calculate ULTRA-REVOLUTIONARY muscle definition intensity based on treatment count
    const muscleIntensity = Math.min(treatmentCount / 40 * 1.5, 1.5); // ULTRA-MASSIVELY increased from 1.0 to 1.5
    
    if (muscleIntensity < 0.05) return; // Skip if too low
    
    // Define abdominal region more precisely
    const centerX = bodyInfo.centerX * ctx.canvas.width;
    const bodyTop = bodyInfo.shouldersY * ctx.canvas.height;
    const bodyBottom = bodyInfo.hipsY * ctx.canvas.height;
    const bodyWidth = (bodyInfo.rightBound - bodyInfo.leftBound) * ctx.canvas.width;
    
    // Abdominal muscle region (core area)
    const absTop = bodyTop + (bodyBottom - bodyTop) * 0.3; // Start 30% down
    const absBottom = bodyTop + (bodyBottom - bodyTop) * 0.8; // End 80% down
    const absWidth = bodyWidth * 0.3; // Narrower for realistic abs
    
    // Create muscle definition pattern
    const imageData = ctx.getImageData(0, 0, ctx.canvas.width, ctx.canvas.height);
    const data = imageData.data;
    
    for (let y = Math.floor(absTop); y < Math.floor(absBottom); y++) {
      for (let x = Math.floor(centerX - absWidth/2); x < Math.floor(centerX + absWidth/2); x++) {
        if (x >= 0 && x < ctx.canvas.width && y >= 0 && y < ctx.canvas.height) {
          
          const relativeX = (x - (centerX - absWidth/2)) / absWidth; // 0 to 1
          const relativeY = (y - absTop) / (absBottom - absTop); // 0 to 1
          
          // Create realistic abdominal muscle pattern
          const centerLineDistance = Math.abs(relativeX - 0.5) * 2; // Distance from center line
          const muscleSegment = Math.sin(relativeY * Math.PI * 3) * 0.5 + 0.5; // 3 segments
          
          // Muscle definition factor
          const muscleFactor = (1 - centerLineDistance) * muscleSegment * muscleIntensity;
          
          // Create subtle shadow/highlight effect for muscle definition
          if (muscleFactor > 0.1) {
            const pixelIndex = (y * ctx.canvas.width + x) * 4;
            
            // Create subtle muscle shadows and highlights
            const shadowFactor = muscleFactor * 0.15; // Subtle shadowing
            const isLeftSide = relativeX < 0.5;
            
            if (isLeftSide) {
              // Left side - slightly darker (shadow)
              data[pixelIndex] = Math.max(0, data[pixelIndex] * (1 - shadowFactor));
              data[pixelIndex + 1] = Math.max(0, data[pixelIndex + 1] * (1 - shadowFactor));
              data[pixelIndex + 2] = Math.max(0, data[pixelIndex + 2] * (1 - shadowFactor));
            } else {
              // Right side - slightly lighter (highlight)
              data[pixelIndex] = Math.min(255, data[pixelIndex] * (1 + shadowFactor * 0.5));
              data[pixelIndex + 1] = Math.min(255, data[pixelIndex + 1] * (1 + shadowFactor * 0.5));
              data[pixelIndex + 2] = Math.min(255, data[pixelIndex + 2] * (1 + shadowFactor * 0.5));
            }
          }
        }
      }
    }
    
    ctx.putImageData(imageData, 0, 0);
  }

  addIndicator(ctx, method, percentage, color) {
    ctx.fillStyle = color;
    ctx.font = 'bold 16px Arial';
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.8)';
    ctx.lineWidth = 3;
    ctx.strokeText(`${method} -${percentage.toFixed(1)}%`, 15, 35);
    ctx.fillText(`${method} -${percentage.toFixed(1)}%`, 15, 35);
  }

  applyEstimatedEnhancements(ctx, img, treatmentCount) {
    
    const tighteningIntensity = Math.min(treatmentCount / 40 * 0.25, 0.25);
    const muscleIntensity = Math.min(treatmentCount / 40 * 0.3, 0.3);
    
    if (tighteningIntensity > 0.03 || muscleIntensity > 0.05) {
      // Estimated abdominal region based on typical body proportions
      const centerX = img.width / 2;
      const abdominalTop = img.height * 0.35;
      const abdominalBottom = img.height * 0.65;
      const abdominalWidth = img.width * 0.25;
      
      // Basic muscle definition
      if (muscleIntensity > 0.05) {
        ctx.save();
        ctx.globalCompositeOperation = 'multiply';
        ctx.globalAlpha = muscleIntensity * 0.1;
        
        // Simple vertical center line
        const gradient = ctx.createLinearGradient(
          centerX - 10, abdominalTop,
          centerX + 10, abdominalTop
        );
        gradient.addColorStop(0, 'rgba(0, 0, 0, 0)');
        gradient.addColorStop(0.5, `rgba(0, 0, 0, ${muscleIntensity * 0.4})`);
        gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
        
        ctx.fillStyle = gradient;
        ctx.fillRect(centerX - 8, abdominalTop, 16, abdominalBottom - abdominalTop);
        
        ctx.restore();
      }
      
      // Basic skin smoothing (simplified version)
      if (tighteningIntensity > 0.03) {
        const imageData = ctx.getImageData(0, 0, img.width, img.height);
        const data = imageData.data;
        
        // Apply subtle smoothing to estimated skin areas
        for (let y = 2; y < img.height - 2; y += 3) { // Skip pixels for performance
          for (let x = 2; x < img.width - 2; x += 3) {
            // Check if in estimated skin region (avoid clothing areas)
            const relativeY = y / img.height;
            if (relativeY > 0.1 && relativeY < 0.9) { // Avoid head and feet
              const pixelIndex = (y * img.width + x) * 4;
              
              // Simple average smoothing
              const smoothingFactor = tighteningIntensity * 0.2;
              const neighbors = [
                data[pixelIndex - 4], data[pixelIndex + 4], // left, right
                data[((y - 1) * img.width + x) * 4], data[((y + 1) * img.width + x) * 4] // up, down
              ];
              const avgR = neighbors.reduce((sum, val, i) => sum + (i % 4 === 0 ? val : 0), 0) / 4;
              
              data[pixelIndex] = data[pixelIndex] * (1 - smoothingFactor) + avgR * smoothingFactor;
            }
          }
        }
        
        ctx.putImageData(imageData, 0, 0);
      }
      
    }
  }

  // REVOLUTIONARY Professional Skin Smoothing Function
  applySkinSmoothing(ctx, img, bodyInfo, weightLossPercentage, treatments) {
    // Calculate ULTRA-MAXIMUM smoothing intensity
    const smoothingIntensity = Math.min(weightLossPercentage * 0.050 + (treatments / 40 * 0.080), 1.2); // ULTRA-REVOLUTIONARY smoothing power
    
    if (smoothingIntensity < 0.02) return; // Skip if too weak
    
    // Get current image data
    const imageData = ctx.getImageData(0, 0, img.width, img.height);
    const data = imageData.data;
    const newData = new Uint8ClampedArray(data);
    
    // Define body region for targeted smoothing
    const centerX = bodyInfo.centerX * img.width;
    const bodyTop = bodyInfo.shouldersY * img.height;
    const bodyBottom = bodyInfo.hipsY * img.height + (bodyInfo.hipsY - bodyInfo.shouldersY) * img.height * 0.3;
    const bodyLeft = bodyInfo.leftBound * img.width;
    const bodyRight = bodyInfo.rightBound * img.width;
    
    // Apply multi-pass bilateral filtering for REVOLUTIONARY smoothing
    for (let pass = 0; pass < 3; pass++) { // Multiple passes for maximum effect
      const passIntensity = smoothingIntensity * (0.6 - pass * 0.1); // Decreasing intensity per pass
      
      for (let y = 3; y < img.height - 3; y++) {
        for (let x = 3; x < img.width - 3; x++) {
          // Check if pixel is in body region
          if (x >= bodyLeft && x <= bodyRight && y >= bodyTop && y <= bodyBottom) {
            const pixelIndex = (y * img.width + x) * 4;
            
            // Calculate distance from body center for falloff
            const distX = (x - centerX) / (bodyRight - bodyLeft);
            const distY = (y - bodyTop) / (bodyBottom - bodyTop);
            const distance = Math.sqrt(distX * distX + distY * distY);
            
            if (distance < 0.8) { // Only smooth pixels close to body center
              // Apply bilateral filter with REVOLUTIONARY strength
              const [newR, newG, newB] = this.bilateralFilter(data, x, y, img.width, img.height, passIntensity);
              
              const blendFactor = passIntensity * (1 - distance);
              newData[pixelIndex] = data[pixelIndex] * (1 - blendFactor) + newR * blendFactor;
              newData[pixelIndex + 1] = data[pixelIndex + 1] * (1 - blendFactor) + newG * blendFactor;
              newData[pixelIndex + 2] = data[pixelIndex + 2] * (1 - blendFactor) + newB * blendFactor;
            }
          }
        }
      }
      
      // Update data for next pass
      data.set(newData);
    }
    
    // Apply the smoothed result
    const finalImageData = new ImageData(newData, img.width, img.height);
    ctx.putImageData(finalImageData, 0, 0);
  }

  // REVOLUTIONARY Bilateral Filter for Professional Skin Smoothing
  bilateralFilter(data, x, y, width, height, intensity) {
    let totalR = 0, totalG = 0, totalB = 0;
    let totalWeight = 0;
    
    const centerIndex = (y * width + x) * 4;
    const centerR = data[centerIndex];
    const centerG = data[centerIndex + 1];
    const centerB = data[centerIndex + 2];
    
    // 7x7 kernel for MAXIMUM smoothing effect
    const radius = 3;
    const spatialSigma = 2.0 * intensity; // Spatial fall-off
    const colorSigma = 25.0 * intensity;  // Color similarity threshold
    
    for (let dy = -radius; dy <= radius; dy++) {
      for (let dx = -radius; dx <= radius; dx++) {
        const nx = x + dx;
        const ny = y + dy;
        
        if (nx >= 0 && nx < width && ny >= 0 && ny < height) {
          const nIndex = (ny * width + nx) * 4;
          const nR = data[nIndex];
          const nG = data[nIndex + 1];
          const nB = data[nIndex + 2];
          
          // Spatial weight (distance-based)
          const spatialDist = dx * dx + dy * dy;
          const spatialWeight = Math.exp(-spatialDist / (2 * spatialSigma * spatialSigma));
          
          // Color weight (similarity-based)
          const colorDist = Math.pow(centerR - nR, 2) + Math.pow(centerG - nG, 2) + Math.pow(centerB - nB, 2);
          const colorWeight = Math.exp(-colorDist / (2 * colorSigma * colorSigma));
          
          // Combined weight
          const weight = spatialWeight * colorWeight;
          
          totalR += nR * weight;
          totalG += nG * weight;
          totalB += nB * weight;
          totalWeight += weight;
        }
      }
    }
    
    return [
      totalWeight > 0 ? totalR / totalWeight : centerR,
      totalWeight > 0 ? totalG / totalWeight : centerG,
      totalWeight > 0 ? totalB / totalWeight : centerB
    ];
  }

  // Cleanup method
  dispose() {
    if (this.detector) {
      this.detector.dispose();
      this.detector = null;
    }
    this.isInitialized = false;
  }
}

export default MoveNetBodyProcessor;